import { useState, useEffect, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { WEEKS, CONTRACT_ARTICLES, DEFAULT_SETTINGS, HORAIRES_RETRAIT } from '../utils/constants'
import { storage } from '../services/storage'
import { appendToSheet } from '../services/googleSheets'
import { supabase } from '../services/supabase'
// generateContractPDF est chargé en import() dynamique au moment de générer un contrat,
// pour ne PAS embarquer jspdf (~350 Ko) dans le bundle initial de la page publique.
import { useModeles } from '../hooks/useModeles'
import { useOptions } from '../hooks/useOptions'
import { countByModele } from '../utils/boards'
import { prixFormule, datesFromStart, dureeLabel, joursDepart, departValide, DUREE_PRESETS } from '../utils/duree'
import { addDays } from '../utils/dates'

// Stripe n'est chargé QU'au moment du paiement (pas à l'accueil) : évite de créer l'iframe cachée
// Stripe sur chaque visite (perf) et surtout l'erreur "null ... contentWindow.postMessage" dans les
// navigateurs in-app restreints (Facebook/Instagram) où cette iframe échoue dès l'ouverture de l'accueil.
let _stripePromise = null
const getStripe = () => {
  if (!_stripePromise) _stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')
  return _stripePromise
}

const WHATSAPP = '33767451718'
const ADDRESS = '17 Rue Docteur Le Gall, 56510 Saint-Pierre-Quiberon'
const MAPS_FALLBACK = 'https://maps.google.com/?q=17+Rue+Docteur+Le+Gall+Saint-Pierre-Quiberon'

const C = {
  bg:     '#FAFAFA',
  white:  '#FFFFFF',
  ink:    '#111111',
  muted:  '#7A7572',
  border: '#E5E0D8',
  accent: '#2B4C7E',
  lin:    '#E8DDD0',
  linD:   '#DDD0BF',
}

const PHOTOS = {
  mid:    '/paddles_quiberon.jpg',
  board1: 'https://bluefinsupboards.fr/cdn/shop/files/Main-Images_Red-Cruise_06d85383-9849-40a0-b3d7-e0de52d8e559.png?v=1782738006&width=1000',
  board2: 'https://bluefinsupboards.fr/cdn/shop/files/Main-Images_Cruise-12.png?v=1782737902&width=1000',
}

const ease = [0.16, 1, 0.3, 1]

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.85, delay, ease },
})

function fullDate(iso) {
  if (!iso) return ''
  const [, m, d] = iso.split('-')
  const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre']
  return `${parseInt(d)} ${months[parseInt(m)-1]}`
}

// Stock de secours si le RPC get_board_stock est indisponible (8× 10'6" + 2× 9'5").
// Le vrai stock est DYNAMIQUE : compté en temps réel sur les planches « disponible »
// (les planches en maintenance sont automatiquement retirées). Voir useBoardStock().
const FALLBACK_STOCK = { '10p6': 8, '9p5': 2 }   // clés = id canonique (= modeles.id / planches.modele)

// Liste des jours [start, end) (end EXCLUSIVE), en UTC pur pour éviter tout décalage
// horaire/DST. Renvoie ['YYYY-MM-DD', …].
function eachDay(start, end) {
  const days = []
  let cur = Date.parse(String(start) + 'T00:00:00Z')
  const stop = Date.parse(String(end) + 'T00:00:00Z')
  if (isNaN(cur) || isNaN(stop)) return days
  while (cur < stop) {
    days.push(new Date(cur).toISOString().slice(0, 10))
    cur += 86400000
  }
  return days
}

// Planches réservées par modèle sur la plage [start, end) = PIC de réservations
// simultanées sur UN seul jour, pas la somme. Une planche louée le lundi puis rendue et
// relouée le mercredi n'immobilise pas 2 planches sur une plage lun→mer : c'est la même,
// libre le mardi. On calcule donc, jour par jour, le nombre réservé, et on garde le
// maximum par modèle. (L'ancienne version additionnait toutes les résas chevauchant la
// plage → « complet » à tort dès qu'on demandait 2-3 jours.)
function bookedOnWeek(week, ranges = []) {
  const days = eachDay(week.start, week.end)
  if (!days.length) days.push(String(week.start))   // sécurité : plage vide → au moins le jour de départ
  const peak = {}
  days.forEach(day => {
    const perDay = {}
    ranges.forEach(r => {
      // Une résa occupe la planche le « day » si date_debut <= day < date_fin (fin exclusive).
      if (r.date_debut && r.date_fin &&
          String(r.date_debut) <= day && String(r.date_fin) > day) {
        const m = countByModele({ lignes: r.lignes, boardSize: r.board_size })
        Object.keys(m).forEach(k => { perDay[k] = (perDay[k] || 0) + m[k] })
      }
    })
    Object.keys(perDay).forEach(k => { peak[k] = Math.max(peak[k] || 0, perDay[k]) })
  })
  return peak
}

// Dispo restante PAR MODÈLE sur une semaine → { '10p6': n, '9p5': m, … }.
function weekAvail(week, ranges = [], stock = FALLBACK_STOCK) {
  const booked = bookedOnWeek(week, ranges)
  const out = {}
  Object.keys(stock).forEach(k => { out[k] = Math.max(0, (Number(stock[k]) || 0) - (booked[k] || 0)) })
  return out
}

// Récupère les périodes réservées (dates + modèle, aucune donnée client)
// via une fonction Supabase accessible au public. Dégrade en silence si absente.
function useBookedRanges() {
  const [ranges, setRanges] = useState([])
  useEffect(() => {
    let on = true
    supabase.rpc('get_booked_ranges').then(({ data, error }) => {
      if (on && !error && Array.isArray(data)) setRanges(data)
    }).catch(() => {})
    return () => { on = false }
  }, [])
  return ranges
}

// Stock louable DYNAMIQUE : nb de planches « disponible » par modèle, en temps réel.
// (Une planche en maintenance est exclue → pas de surbooking caché.)
function useBoardStock() {
  const [stock, setStock] = useState(FALLBACK_STOCK)
  useEffect(() => {
    let on = true
    supabase.rpc('get_board_stock').then(({ data, error }) => {
      // Repli (FALLBACK_STOCK) UNIQUEMENT si la requête échoue. Une réponse valide même vide
      // = stock réel (0 dispo), pour ne pas afficher un faux stock quand tout est hors service.
      if (!on || error || !Array.isArray(data)) return
      const s = {}
      data.forEach(r => { if (r.modele) s[r.modele] = Number(r.dispo) || 0 })
      setStock(s)
    }).catch(() => {})
    return () => { on = false }
  }, [])
  return stock
}

// Caution par planche, lue depuis app_config (même source que le serveur). Défaut 200 € si absente.
function useCautionPerBoard() {
  const [caution, setCaution] = useState(200)
  useEffect(() => {
    let on = true
    supabase.from('app_config').select('value').eq('key', 'caution').maybeSingle().then(({ data }) => {
      if (!on || !data) return
      let v = data.value
      try { v = typeof v === 'string' ? JSON.parse(v) : v } catch { /* valeur brute */ }
      const n = Number(v)
      if (n > 0) setCaution(n)
    }).catch(() => {})
    return () => { on = false }
  }, [])
  return caution
}

// Statut d'une semaine (vue liste) = total des places restantes tous modèles confondus.
function weekStatus(week, ranges = [], stock = FALLBACK_STOCK) {
  const avail = weekAvail(week, ranges, stock)
  const available = Object.values(avail).reduce((s, n) => s + n, 0)
  if (available === 0) return { label: 'Complet', color: '#C0392B', dot: '#C0392B', available: 0 }
  if (available <= 2)  return { label: `${available} place${available > 1 ? 's' : ''}`, color: '#C87941', dot: '#C87941', available }
  return { label: 'Disponible', color: '#2D7A4F', dot: '#2D7A4F', available }
}

/* ────────── STRIPE PAYMENT FORM ────────── */
function StripePaymentForm({ stripeSecrets, form, contract, onSuccess, onError }) {
  const stripe   = useStripe()
  const elements = useElements()
  const [paying, setPaying] = useState(false)

  const handlePay = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setPaying(true)
    onError('')
    try {
      // 0. Persister les données AVANT le paiement : si la carte exige une
      //    authentification 3D Secure avec REDIRECTION, la page se recharge et on
      //    perdrait la signature + l'état. On les récupère au retour (voir Landing).
      try {
        localStorage.setItem('paddle_pending_3ds', JSON.stringify({
          paymentIntentId: stripeSecrets.rentalClientSecret?.split('_secret_')[0],
          signingToken: contract.signingToken,
          signature: contract.signatureContrat,
          contractId: contract.id,
          display: {
            firstName: contract.firstName, lastName: contract.lastName, email: contract.email,
            boardSize: contract.boardSize, startDate: contract.startDate, endDate: contract.endDate,
            price: contract.price, caution: contract.caution,
          },
        }))
      } catch {}

      // 1. Confirmer le paiement de la location
      const { error: rentalError } = await stripe.confirmPayment({
        elements,
        // return_url PROPRE (origin + chemin, SANS hash ni query) : si l'URL contenait une
        // ancre (#semaines) ou d'anciens paramètres, Stripe collerait ses paramètres de retour
        // après le # → window.location.search vide au retour → l'écran de finalisation ne se
        // déclenchait jamais et le client revenait sur une page « cassée » après avoir payé.
        confirmParams: { return_url: window.location.origin + window.location.pathname },
        redirect: 'if_required',
      })
      if (rentalError) throw new Error(rentalError.message)

      // NB : la caution n'est PLUS bloquée ici. La carte est sauvegardée (setup_future_usage)
      // et le blocage de caution est créé par l'ADMIN au check-in (J-0) — une pré-auth Stripe
      // expirant en ~7 j, la bloquer au paiement (parfois des semaines avant) serait inutile.

      // Sauvegarder le contrat avec les IDs Stripe
      const fullContract = {
        ...contract,
        stripeRentalId:   stripeSecrets.rentalClientSecret?.split('_secret_')[0],
        stripeCautionId:  null, // créé au check-in
        stripeCustomerId: stripeSecrets.customerId,
      }
      // a) Enregistrement SÉCURISÉ côté serveur : la fonction vérifie le PaymentIntent
      //    Stripe (réellement payé) puis insère avec service_role + pose la signature.
      //    Le navigateur ne peut plus forger une résa "paid" (la clé anon ne l'autorise plus).
      try {
        await fetch('/.netlify/functions/confirm-reservation', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentIntentId: fullContract.stripeRentalId,
            signature: fullContract.signatureContrat,
          }),
        })
      } catch (e) { console.warn('confirm-reservation:', e?.message) }
      // b) Cache local (sert au calcul des disponibilités du site)
      storage.addContract(fullContract)

      // c) PDF + email client — best-effort en arrière-plan (ne bloque pas l'écran de succès)
      ;(async () => {
        try {
          const { data: ownerSig } = await supabase.rpc('get_owner_signature')
          const { generateContractPDF } = await import('../services/pdfGenerator')
          const doc = generateContractPDF(
            { ...fullContract, boardModel: fullContract.boardName || fullContract.boardSize, signature: fullContract.signatureContrat },
            { ...DEFAULT_SETTINGS, ownerSignature: ownerSig || '' }
          )
          const pdfBase64 = doc.output('datauristring').split(',')[1]
          await fetch('/.netlify/functions/confirm-signature', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: fullContract.id, token: fullContract.signingToken, pdfBase64 }),
          })
        } catch (_) { /* best-effort */ }
      })()

      // Pas de redirection 3DS → flux normal terminé, on retire les données de secours.
      try { localStorage.removeItem('paddle_pending_3ds') } catch {}
      onSuccess()
    } catch (err) {
      onError(err.message || 'Le paiement a échoué. Veuillez réessayer.')
    } finally { setPaying(false) }
  }

  return (
    <form onSubmit={handlePay}>
      <PaymentElement options={{ layout: 'tabs' }} />
      <p style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', margin: '16px 0 0', lineHeight: 1.5 }}>
        En cliquant sur Payer, vous acceptez nos{' '}
        <a href="/cgv" target="_blank" rel="noreferrer" style={{ color: '#2B4C7E', textDecoration: 'underline' }}>Conditions Générales de Vente</a>.
      </p>
      <motion.button type="submit" disabled={!stripe || paying}
        whileHover={{ opacity: 0.88 }} whileTap={{ scale: 0.98 }}
        style={{
          marginTop: 24, width: '100%', padding: '16px', borderRadius: 8, border: 'none',
          background: (!stripe || paying) ? '#ccc' : '#2B4C7E',
          fontSize: 15, fontWeight: 700, color: '#fff', cursor: !stripe || paying ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
        {paying ? 'Paiement en cours…' : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            Payer maintenant
          </>
        )}
      </motion.button>
    </form>
  )
}

/* ────────── MODAL WIZARD (style Airbnb) ────────── */
// La SIGNATURE est une étape à part entière (4), pas un bloc noyé dans le paiement.
// Avant : l'étape Paiement empilait récap + contrat + case + signature + carte sur un
// seul écran de ~5 hauteurs de téléphone — on faisait défiler par-dessus le contrat et
// on payait sans signer (cas Manon, 14/07). On ne peut pas sauter une étape.
const STEPS = [
  { id: 1, label: 'Départ' },
  { id: 2, label: 'Planche' },
  { id: 3, label: 'Toi' },
  { id: 4, label: 'Résumé' },
  { id: 5, label: 'Contrat' },
  { id: 6, label: 'Paiement' },
]

/* Pad de signature pour le tunnel */
function MiniSignaturePad({ onChange }) {
  const ref = useRef(null)
  const drawing = useRef(false)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.strokeStyle = '#111'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'
    const pos = e => { const r = canvas.getBoundingClientRect(); const t = e.touches?.[0] || e; return { x: t.clientX - r.left, y: t.clientY - r.top } }
    const start = e => { e.preventDefault(); drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y) }
    const move = e => { e.preventDefault(); if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke() }
    const end = () => { drawing.current = false; onChange(canvas.toDataURL('image/png')) }
    canvas.addEventListener('mousedown', start); canvas.addEventListener('mousemove', move); canvas.addEventListener('mouseup', end)
    canvas.addEventListener('touchstart', start, { passive: false }); canvas.addEventListener('touchmove', move, { passive: false }); canvas.addEventListener('touchend', end)
    return () => { canvas.removeEventListener('mousedown', start); canvas.removeEventListener('mousemove', move); canvas.removeEventListener('mouseup', end); canvas.removeEventListener('touchstart', start); canvas.removeEventListener('touchmove', move); canvas.removeEventListener('touchend', end) }
  }, [onChange])
  const clear = () => { const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); onChange(null) }
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Votre signature</span>
        <button type="button" onClick={clear} style={{ fontSize: 12, color: '#2B4C7E', background: 'none', border: 'none', cursor: 'pointer' }}>Effacer</button>
      </div>
      <canvas ref={ref} width={500} height={150}
        style={{ width: '100%', height: 150, background: '#fafafa', borderRadius: 10, border: '1.5px solid #E5E0D8', touchAction: 'none' }} />
    </div>
  )
}

function useWeeksWithLivePrices() {
  const [weeks, setWeeks] = useState(WEEKS)
  useEffect(() => {
    supabase.from('app_config').select('value').eq('key', 'weeks_prices').single()
      .then(({ data }) => {
        if (!data?.value) return
        try {
          const prices = JSON.parse(data.value) // [{ id, price, price12 }]
          setWeeks(WEEKS.map(w => {
            const p = prices.find(x => x.id === w.id)
            return p ? { ...w, price: p.price, price12: p.price12 ?? w.price12 } : w
          }))
        } catch { /* silently keep WEEKS from constants */ }
      })
  }, [])
  return weeks
}

// Libellé lisible d'un créneau de rotation pour l'affichage.
function creneauLabel(c) {
  const f = c.formule === 'weekend' ? 'Weekend' : '2 jours'
  return `${jourMois(c.retrait)} → ${jourMois(c.retour)} · ${f}`
}
function daysBetween(a, b) {
  const [ya, ma, da] = String(a).split('-').map(Number)
  const [yb, mb, db] = String(b).split('-').map(Number)
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000)
}
const JOURS = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam']
// "lun 6" (jour de semaine + numéro) — pour les chips sous un en-tête de semaine.
function jourCourt(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number)
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return `${JOURS[dow]} ${d}`
}
// "lun 6 juil" (jour + numéro + mois) — quand le contexte de semaine n'est pas affiché (récap, tunnel).
function jourMois(dateStr) {
  return `${jourCourt(dateStr)} ${(fullDate(dateStr).split(' ')[1] || '')}`
}
// Lundi (ISO) de la semaine contenant une date — pour regrouper les créneaux par semaine.
function mondayOf(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  const dow = dt.getUTCDay()
  dt.setUTCDate(dt.getUTCDate() + (dow === 0 ? -6 : 1 - dow))
  return dt.toISOString().slice(0, 10)
}
// Modèle de réservation + config formules + saison (app_config). Aperçu sans toucher la base via ?modele=formules.
function useReservationModel() {
  // Modèle « durée libre » ('jours') par défaut : le client choisit une date de départ + une durée.
  // app_config (ci-dessous) confirme/ajuste ensuite ; ?modele= force toujours.
  const [cfg, setCfg] = useState({ modele: 'jours', formules: DEFAULT_SETTINGS.formules, saison: DEFAULT_SETTINGS.saison, prixJourSupp: DEFAULT_SETTINGS.prixJourSupp })
  useEffect(() => {
    let on = true
    supabase.from('app_config').select('key, value').in('key', ['modele', 'formules', 'saison', 'prix_jour_supp']).then(({ data }) => {
      if (!on || !Array.isArray(data)) return
      const byKey = {}
      data.forEach(r => { try { byKey[r.key] = typeof r.value === 'string' ? JSON.parse(r.value) : r.value } catch { byKey[r.key] = r.value } })
      setCfg(prev => ({
        modele: byKey.modele || prev.modele,
        formules: byKey.formules ? { ...prev.formules, ...byKey.formules } : prev.formules,
        saison: byKey.saison ? { ...prev.saison, ...byKey.saison } : prev.saison,
        prixJourSupp: byKey.prix_jour_supp ? { ...prev.prixJourSupp, ...byKey.prix_jour_supp } : prev.prixJourSupp,
      }))
    }).catch(() => {})
    return () => { on = false }
  }, [])
  const forced = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('modele') : null
  return forced ? { ...cfg, modele: forced } : cfg
}

function ReservationModal({ onClose, initialBoardSize, initialPromoCode = '', initialSejourOk = false, initialDuree = 1, initialStartDate = '', initialStep = 1 }) {
  const bookedRanges = useBookedRanges()
  const stock = useBoardStock()
  const cautionPerBoard = useCautionPerBoard()
  const modeles = useModeles()
  const optionsList = useOptions()
  const { formules, saison, prixJourSupp } = useReservationModel()
  // Jour courant en calendrier LOCAL, formaté en ISO via UTC midi-nuit → pas d'off-by-one près de minuit.
  const _now = new Date()
  const todayISO = new Date(Date.UTC(_now.getFullYear(), _now.getMonth(), _now.getDate())).toISOString().slice(0, 10)
  const [step, setStep] = useState(initialStep)
  const [dir, setDir]   = useState(1) // 1=forward, -1=backward
  // Quantités par modèle (générique) : { '10p6': 1, '9p5': 0, … }. Pré-sélection éventuelle
  // depuis une fiche planche (initialBoardSize = label "10'6\"" / "9'5\"").
  const initialQtys = (() => {
    if (!initialBoardSize) return {}
    const m = modeles.find(x => x.label === initialBoardSize)
      || modeles[initialBoardSize.includes('9') ? 1 : 0]
    return m ? { [m.id]: 1 } : {}
  })()
  const [form, setForm] = useState({
    firstName:'', lastName:'', email:'', phone:'',
    startDate: initialStartDate,  // 'AAAA-MM-JJ' — date de départ (pré-remplie depuis la rubrique tarifs)
    dureeJours: initialDuree,     // durée en jours (pré-sélectionnée depuis la rubrique tarifs)
    promoCode: initialPromoCode,
    qtys: initialQtys,
    optionQtys: {},
    message:''
  })
  const [promoInfo, setPromoInfo] = useState(null) // { ok, type, reason } après pré-validation
  const [sejourOk, setSejourOk] = useState(initialSejourOk)  // true = code séjour valide appliqué → tarif séjour
  const [sending, setSending] = useState(false)
  const [sent, setSent]       = useState(false)
  const [error, setError]     = useState('')
  const [stripeSecrets, setStripeSecrets] = useState(null) // { rentalClientSecret, cautionClientSecret }
  const [signingToken, setSigningToken]   = useState(null) // jeton de signature du contrat
  const [contractSig, setContractSig]     = useState(null) // signature client (étape Contrat)
  const [contractOk, setContractOk]       = useState(false) // case "j'accepte"
  const [showPromo, setShowPromo]         = useState(!!initialPromoCode) // code séjour replié par défaut (ne pas inquiéter)
  const [dateChoice, setDateChoice]       = useState(null)  // 'today' | 'tomorrow' | 'cal' — raccourci de date actif
  const dateInputRef = useRef(null)
  const scrollRef    = useRef(null)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  // Modèle « durée libre » : UNE location = date de départ + durée. On reconstruit un tableau
  // selectedWeeks à UN élément (même forme id/label/start/end/price) → tout l'aval du tunnel
  // (récap, paiement, succès) fonctionne sans réécriture.
  const tarif = sejourOk ? 'sejour' : 'public'
  const { date_debut, date_fin } = form.startDate ? datesFromStart(form.startDate, form.dureeJours) : { date_debut: '', date_fin: '' }
  const prixParPlanche = form.startDate ? prixFormule(form.dureeJours, tarif, { formules, prixJourSupp }) : 0
  const selectedWeeks  = form.startDate ? [{
    id: 'loc', label: dureeLabel(form.dureeJours, formules), start: date_debut, end: date_fin,
    price: prixParPlanche,
    prix_public: prixFormule(form.dureeJours, 'public', { formules, prixJourSupp }),
    prix_sejour: prixFormule(form.dureeJours, 'sejour', { formules, prixJourSupp }),
  }] : []
  const totalBoards    = Object.values(form.qtys).reduce((s, n) => s + (Number(n) || 0), 0)
  const totalPrice     = prixParPlanche * totalBoards
  // Facteur selon l'unité de l'option : "par semaine" × nb de locations (1), "par jour" × durée,
  // "par planche" × nb de planches, "par réservation"/"par location" (défaut) × 1.
  const nbWeeks        = selectedWeeks.length   // 1 si location, 0 sinon
  const nbJours        = form.dureeJours
  const optionFactor   = (unite) => unite === 'par semaine' ? Math.max(1, nbWeeks)
    : unite === 'par jour' ? Math.max(1, nbJours)
    : unite === 'par planche' ? Math.max(1, totalBoards) : 1
  const optionItems    = optionsList
    .filter(o => (Number(form.optionQtys[o.id]) || 0) > 0)
    .map(o => {
      const qty = Number(form.optionQtys[o.id])
      const prix = Number(o.prix) || 0
      const facteur = optionFactor(o.unite)
      return { id: o.id, nom: o.nom, qty, prix, unite: o.unite || 'par réservation', facteur, total: prix * qty * facteur }
    })
  const optionsTotal   = optionItems.reduce((s, o) => s + o.total, 0)
  const grandTotal     = totalPrice + optionsTotal

  const setOptionQty = (id, delta) => setForm(f => {
    const cur = Number(f.optionQtys[id]) || 0
    return { ...f, optionQtys: { ...f.optionQtys, [id]: Math.max(0, cur + delta) } }
  })

  // Dispo par modèle sur la plage [date_debut, date_fin) de la location → { modeleId: n }.
  const avail = useMemo(() => {
    if (!form.startDate) return { ...stock }
    return weekAvail({ start: date_debut, end: date_fin }, bookedRanges, stock)
  }, [form.startDate, date_debut, date_fin, bookedRanges, stock])

  const setQty = (id, delta) => setForm(f => {
    const max = avail[id] ?? 0
    const cur = Number(f.qtys[id]) || 0
    return { ...f, qtys: { ...f.qtys, [id]: Math.min(max, Math.max(0, cur + delta)) } }
  })

  // Si la dispo se réduit (changement de semaines), on rabote les quantités déjà saisies.
  const availKey = modeles.map(m => avail[m.id] ?? 0).join(',')
  useEffect(() => {
    setForm(f => {
      let changed = false
      const q = { ...f.qtys }
      Object.keys(q).forEach(id => {
        const c = Math.min(Number(q[id]) || 0, avail[id] ?? 0)
        if (c !== q[id]) { q[id] = c; changed = true }
      })
      return changed ? { ...f, qtys: q } : f
    })
  }, [availKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Si la durée augmente et que la date de départ choisie ne rentre plus dans la saison
  // (retour > dernier retour autorisé), on efface la date pour forcer un nouveau choix valide.
  useEffect(() => {
    if (form.startDate && !departValide(form.startDate, form.dureeJours, saison)) {
      setForm(f => ({ ...f, startDate: '' }))
    }
  }, [form.dureeJours, form.startDate, saison.start, saison.lastReturn]) // eslint-disable-line react-hooks/exhaustive-deps

  // À chaque changement d'étape, on repart du HAUT.
  // Sans ça, le défilement était conservé : un client qui descend à l'étape Planche pour voir
  // les options (elles sont coupées) arrivait à l'étape suivante déjà défilé de 128 px, titre
  // hors écran — et ratait le haut de chaque étape, dont le code séjour.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [step])

  // Quand la date est effacée (ex. durée qui dépasse la saison), on remet à zéro l'état visuel des raccourcis.
  useEffect(() => {
    if (!form.startDate) { setDateChoice(null) }
  }, [form.startDate])

  // Applique une date de départ depuis un raccourci (Aujourd'hui / Demain) ou le calendrier.
  const pickStart = (iso, choice) => {
    if (!iso) return
    if (!departValide(iso, form.dureeJours, saison)) {
      setError('Cette date ne permet pas de terminer la location avant la fin de saison.')
      return
    }
    setError(''); setDateChoice(choice)
    setForm(f => ({ ...f, startDate: iso }))
    if (step === 1) next()   // auto-avance vers Planche dès qu'une date valide est choisie
  }

  const boardDesc = modeles
    .filter(m => (Number(form.qtys[m.id]) || 0) > 0)
    .map(m => `${form.qtys[m.id]}× ${m.label}`)
    .join(' + ') || '—'

  const goTo = (n) => {
    // Revenir en arrière pour MODIFIER la réservation invalide la signature : on a signé
    // un contrat portant des dates, des planches et un prix précis. Changer de planche
    // après coup et payer avec l'ancienne signature n'aurait aucun sens.
    if (n < 5 && step >= 5 && contractSig) { setContractSig(null); setContractOk(false) }
    setDir(n > step ? 1 : -1); setError(''); setStep(n)
  }
  const next  = () => goTo(step + 1)
  const back  = () => goTo(step - 1)

  // Appelé en quittant l'étape 2 — crée les PaymentIntents Stripe puis passe à l'étape 3 (Contrat & paiement)
  const goToPayment = async () => {
    setError(''); setSending(true)
    try {
      const contractId = `demande-${Date.now()}`
      const token = (crypto?.randomUUID?.() || `${contractId}-${Math.round(performance.now())}`)
      setSigningToken(token)
      const lignesPayload = modeles
        .filter(m => (Number(form.qtys[m.id]) || 0) > 0)
        .map(m => ({ modele: m.id, qty: Number(form.qtys[m.id]) }))
      const payload = {
        lignes: lignesPayload,
        options: optionItems,
        customerEmail: form.email,
        customerName: `${form.firstName} ${form.lastName}`,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone || '',
        startDate: date_debut,
        dureeJours: form.dureeJours,
        promoCode: form.promoCode || '',
        endDate: date_fin,
        weeks: dureeLabel(form.dureeJours, formules),
        boards: boardDesc,
        message: form.message || '',
        contractId,
        signingToken: token,
      }
      const res = await fetch('/.netlify/functions/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Erreur serveur')
      setStripeSecrets({ ...data })
      goTo(6)
    } catch (e) {
      setError(`Impossible d'initialiser le paiement : ${e.message}`)
    } finally { setSending(false) }
  }

  // Pré-validation du code promo (affiche la remise avant paiement). La vérité reste le serveur au paiement.
  const applyPromo = async () => {
    if (!form.promoCode) return
    try {
      const res = await fetch('/.netlify/functions/validate-promo', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: form.promoCode }),
      })
      const data = await res.json()
      setPromoInfo(data)
      setSejourOk(!!(data.ok && data.type === 'sejour'))
    } catch { setPromoInfo({ ok: false, reason: 'erreur' }); setSejourOk(false) }
  }

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)

  // Contrat accepté ET signé : condition pour débloquer le formulaire de paiement (étape 4).
  // Tant que c'est faux, Stripe n'est pas monté du tout — on ne peut pas payer sans signer.
  const contractReady = contractOk && !!contractSig

  const canNext = () => {
    if (step === 1) return !!form.startDate
    if (step === 2) return totalBoards > 0
    if (step === 3) return form.firstName && form.lastName && emailValid
    if (step === 4) return true            // Résumé : on relit, rien à saisir
    if (step === 5) return contractReady   // pas de passage au paiement sans contrat signé
    return true
  }

  const handleSubmit = async () => {
    setError(''); setSending(true)
    try {
      const id    = `demande-${Date.now()}`
      const contract = {
        id, status: 'demande',
        firstName: form.firstName, lastName: form.lastName,
        email: form.email, phone: form.phone || '',
        weekId: '',
        startDate: date_debut, endDate: date_fin,
        price: grandTotal, caution: cautionPerBoard * totalBoards,
        boardId: '', serial: '',
        boardSize: boardDesc, message: form.message || '',
        lignes: modeles
          .filter(m => (Number(form.qtys[m.id]) || 0) > 0)
          .map(m => ({ modele: m.id, qty: Number(form.qtys[m.id]) })),
        createdAt: new Date().toISOString(), boardName: boardDesc,
      }
      // Sauvegarde locale
      storage.addContract(contract)
      // Sync Google Sheets (optionnel)
      try { await appendToSheet(null, null, contract) } catch {}
      // L'email de confirmation client est envoyé CÔTÉ SERVEUR (Resend) par confirm-reservation
      // — fiable, couvre aussi le chemin 3D Secure. (Ancien envoi EmailJS retiré : jamais configuré + clé erronée.)
      setSent(true)
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer ou nous contacter directement.')
    } finally { setSending(false) }
  }

  const inputStyle = {
    width: '100%', padding: '14px 0', fontSize: 16, color: C.ink,
    background: 'transparent', border: 'none',
    borderBottom: `1.5px solid ${C.border}`, outline: 'none',
    fontFamily: 'DM Sans, sans-serif', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  }

  // Bouton « raccourci » de l'étape 1 (Aujourd'hui / Demain / Choisir une date).
  const qkStyle = (on) => ({
    border: `1.5px solid ${on ? C.accent : C.border}`,
    background: on ? C.accent : C.white,
    color: on ? '#fff' : C.accent,
    fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13,
    padding: '10px 14px', borderRadius: 11, cursor: 'pointer', transition: 'all 0.15s',
  })
  // Kicker (petit sur-titre) réutilisé dans l'étape 1.
  const kickerStyle = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.muted, margin: '0 0 12px' }

  const variants = {
    enter:  (d) => ({ opacity: 0, x: d > 0 ? 48 : -48 }),
    center: { opacity: 1, x: 0 },
    exit:   (d) => ({ opacity: 0, x: d > 0 ? -48 : 48 }),
  }

  /* ── layout ── */
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, height: '100dvh', zIndex: 300, background: C.white, display: 'flex', flexDirection: 'column' }}>

      {/* ── HEADER ── */}
      <div style={{ flexShrink: 0, background: C.white, borderBottom: `1px solid ${C.border}`, padding: '0 24px' }}>
        {/* Logo + Fermer */}
        <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <img src="/plouf_go_logo_profil_carre.png" alt="Plouf and Go" style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
          <button onClick={onClose} aria-label="Fermer"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: C.muted, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, letterSpacing: '0.08em' }}>
            FERMER
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <line x1="1" y1="1" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5"/>
              <line x1="13" y1="1" x2="1" y2="13" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </button>
        </div>
        {/* Barre de progression */}
        {!sent && (
          <div style={{ display: 'flex', gap: 6, paddingBottom: 16 }}>
            {STEPS.map((s) => (
              <div key={s.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                <div style={{
                  height: 3, width: '100%', borderRadius: 99,
                  background: step >= s.id ? C.accent : C.border,
                  transition: 'background 0.3s',
                }} />
                <span style={{
                  fontSize: 10, fontWeight: step === s.id ? 700 : 400,
                  color: step === s.id ? C.accent : C.muted,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  transition: 'color 0.2s',
                }}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── CONTENU DÉFILANT ── */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px 120px' }}>
          <AnimatePresence mode="wait" custom={dir}>
            {sent ? (
              <motion.div key="sent" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                style={{ textAlign: 'center', paddingTop: 48 }}>
                <motion.div
                  initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.1 }}
                  style={{ width: 72, height: 72, borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 32px' }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12l5 5L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.div>
                <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 36, color: C.ink, letterSpacing: '-1px', marginBottom: 16 }}>
                  Félicitations {form.firstName} !
                </h2>
                <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.8, maxWidth: 400, margin: '0 auto 24px' }}>
                  Votre réservation est confirmée. Nous vous attendons le{' '}
                  <strong style={{ color: C.ink }}>
                    {selectedWeeks[0] ? fullDate(selectedWeeks[0].start) : ''}
                  </strong>{' '}
                  au {ADDRESS.replace(', 56510', ',')}.<br/>Nos permanences : {HORAIRES_RETRAIT} — dites-nous votre heure d'arrivée, on s'organise.
                </p>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ADDRESS)}`}
                  target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: '0 auto 24px', padding: '10px 18px', borderRadius: 10, background: C.ink, color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
                  🗺️ Voir l'itinéraire
                </a>
                <div style={{ background: C.lin, borderRadius: 8, padding: '20px 24px', maxWidth: 380, margin: '0 auto 28px', textAlign: 'left' }}>
                  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, margin: '0 0 12px' }}>Votre réservation</p>
                  <p style={{ fontSize: 14, fontWeight: 600, color: C.ink, margin: '0 0 6px' }}>{form.firstName} {form.lastName}</p>
                  <p style={{ fontSize: 13, color: C.muted, margin: '0 0 4px' }}>
                    📅 {selectedWeeks.map(w => w.label).join(' + ')}
                  </p>
                  <p style={{ fontSize: 13, color: C.muted, margin: '0 0 4px' }}>🏄 {boardDesc}</p>
                  <p style={{ fontSize: 13, color: C.muted, margin: '0 0 4px' }}>💳 {totalPrice}€ payés · caution {cautionPerBoard*totalBoards}€ (bloquée au retrait)</p>
                  <p style={{ fontSize: 12, color: C.muted, margin: '12px 0 0', fontStyle: 'italic' }}>
                    Un email de confirmation vous a été envoyé à {form.email}
                  </p>
                </div>
                <button onClick={() => { setSent(false); setStep(1); setContractSig(null); setContractOk(false); setSigningToken(null); setStripeSecrets(null); setForm({ firstName:'', lastName:'', email:'', phone:'', startDate:'', dureeJours:1, promoCode:'', qtys:{}, optionQtys:{}, message:'' }) }}
                  style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.accent, background: 'none', border: `1.5px solid ${C.accent}`, cursor: 'pointer', padding: '12px 28px', borderRadius: 4 }}>
                  Nouvelle réservation
                </button>
              </motion.div>

            ) : step === 1 ? (
              <motion.div key="step1" custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.28, ease: [0.16,1,0.3,1] }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.accent, marginBottom: 10 }}>Étape {step} / {STEPS.length}</p>
                <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.8rem,4vw,2.6rem)', letterSpacing: '-1px', color: C.ink, marginBottom: 6 }}>
                  C'est pour quand, le plouf ?
                </h2>
                <p style={{ fontSize: 14, color: C.muted, marginBottom: 14, lineHeight: 1.6 }}>
                  Choisis ta durée et le jour. La planche reste avec toi tout le temps de la loc — pagaie, pompe, leash, gilet, tout est dedans.
                </p>
                {/* Réassurance : désamorce la peur (caution = empreinte, pas un débit) */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
                  {[`Caution ${cautionPerBoard}€ en empreinte, jamais débitée`, 'Tout inclus'].map(t => (
                    <span key={t} style={{ fontSize: 11.5, fontWeight: 600, color: C.accent, background: 'rgba(93,216,240,0.18)', borderRadius: 999, padding: '5px 11px' }}>✓ {t}</span>
                  ))}
                </div>

                {/* (a) DATE d'abord — raccourcis + « Choisir une date » = label ⊃ input natif (fiable mobile) */}
                <p style={kickerStyle}>Tu pars quel jour ?</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[{ k: 'today', iso: todayISO, label: "Aujourd'hui" }, { k: 'tomorrow', iso: addDays(todayISO, 1), label: 'Demain' }].map(b => (
                    <button key={b.k} type="button" onClick={() => pickStart(b.iso, b.k)} style={qkStyle(dateChoice === b.k)}>{b.label}</button>
                  ))}
                  {(() => {
                    const minDate = (saison?.start && saison.start > todayISO) ? saison.start : todayISO
                    const jours = joursDepart(saison, form.dureeJours, todayISO)
                    const maxDate = jours.length ? jours[jours.length - 1] : ''
                    return (
                      <label
                        onClick={() => {
                          // Chrome/Edge sur ordinateur n'ouvrent le calendrier QUE si on clique
                          // l'icône du champ — ici invisible (opacity:0) et recouverte par le label.
                          // Le clic donnait donc le focus à un champ transparent : rien ne s'ouvrait.
                          // Safari et les mobiles ouvrent au clic n'importe où, d'où le bug invisible
                          // en test. On force l'ouverture, SANS preventDefault : le comportement
                          // natif reste intact là où il marche déjà.
                          try { dateInputRef.current?.showPicker?.() } catch { /* déjà ouvert ou non supporté */ }
                        }}
                        style={{ ...qkStyle(dateChoice === 'cal'), position: 'relative', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                        {dateChoice === 'cal' && form.startDate ? `📅 ${fullDate(form.startDate)}` : '📅 Choisir une date'}
                        <input ref={dateInputRef} type="date" value={form.startDate} min={minDate} max={maxDate || undefined}
                          onChange={e => {
                            // NE PAS auto-avancer ici : sur iOS onChange se déclenche à chaque cran de molette
                            // (fermerait le calendrier avant le choix). On enregistre juste la date au fil du défilement.
                            const v = e.target.value
                            if (!v) { setError(''); setForm(f => ({ ...f, startDate: '' })); return }
                            if (!departValide(v, form.dureeJours, saison)) {
                              setError('Cette date ne permet pas de terminer la location avant la fin de saison.')
                              return
                            }
                            setError(''); setDateChoice('cal'); setForm(f => ({ ...f, startDate: v }))
                          }}
                          onBlur={e => {
                            // Auto-avance une fois le calendrier REFERMÉ (choix terminé), pas pendant le défilement.
                            const v = e.target.value
                            if (v && departValide(v, form.dureeJours, saison) && step === 1) next()
                          }}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 'none', padding: 0, margin: 0 }} />
                      </label>
                    )
                  })()}
                </div>
                {error && (
                  <p style={{ fontSize: 13, color: '#C0392B', padding: '10px 14px', background: '#FEF2F2', borderRadius: 6, marginTop: 12 }}>{error}</p>
                )}
              </motion.div>

            ) : step === 2 ? (
              <motion.div key="step2" custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.28, ease: [0.16,1,0.3,1] }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.accent, marginBottom: 10 }}>Étape {step} / {STEPS.length}</p>
                <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.8rem,4vw,2.6rem)', letterSpacing: '-1px', color: C.ink, marginBottom: 6 }}>
                  Ta planche
                </h2>
                {/* Une seule ligne : « choisis ton modèle et la quantité » est déjà évident en
                    regardant les cartes. Sur 390 px, la phrase d'origine en prenait deux (42 px). */}
                <p style={{ fontSize: 14, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
                  Pagaie, leash, pompe et sac inclus.
                </p>
                {selectedWeeks.length > 0 && modeles.every(m => (avail[m.id] ?? 0) === 0) && (
                  <div style={{ marginBottom: 20, padding: '14px 16px', borderRadius: 10, background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#C0392B', marginBottom: 4 }}>Complet sur cette période 😔</div>
                    <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                      Toutes nos planches sont déjà réservées sur les dates choisies. Reviens à l'étape précédente pour choisir d'autres dates, ou contacte-nous par WhatsApp en cas d'annulation.
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {modeles.map((m, idx) => {
                    const qty = Number(form.qtys[m.id]) || 0
                    const dispo = avail[m.id] ?? 0
                    const label = m.nom_complet || `Bluefin ${m.label}`
                    const sub = m.sous_titre || ''
                    const capacity = m.capacite_kg ? `${m.capacite_kg} kg max` : ''
                    const photo = m.photo_url || (idx === 0 ? PHOTOS.board1 : PHOTOS.board2)
                    const soldOut = dispo === 0
                    const atMax   = qty >= dispo
                    return (
                    <div key={m.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10,
                      border: `2px solid ${qty > 0 ? C.accent : C.border}`,
                      background: soldOut ? '#f7f7f7' : (qty > 0 ? 'rgba(43,76,126,0.06)' : C.white),
                      transition: 'all 0.15s',
                      opacity: soldOut ? 0.55 : 1,
                      boxShadow: qty > 0 ? `0 0 0 4px rgba(43,76,126,0.08)` : 'none',
                    }}>
                      <img src={photo} alt={label} style={{ width: 50, height: 50, borderRadius: 6, objectFit: 'contain', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{label}</div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{sub}</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2, fontWeight: 700 }}>{capacity}</div>
                        <div style={{ fontSize: 11, marginTop: 4, fontWeight: 700, color: soldOut ? '#C0392B' : (dispo <= 2 ? '#C87941' : '#2D7A4F') }}>
                          {!selectedWeeks.length ? '' : soldOut ? 'Complet sur ce créneau' : `${dispo} dispo`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                        <button type="button" onClick={() => setQty(m.id, -1)} disabled={qty===0}
                          style={{ width: 34, height: 34, borderRadius: '50%', border: `1.5px solid ${C.border}`, background: qty===0 ? '#f5f5f5' : C.white, cursor: qty===0 ? 'not-allowed' : 'pointer', fontSize: 20, color: qty===0 ? C.muted : C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          −
                        </button>
                        <span style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 800, fontSize: 20, color: C.ink, minWidth: 24, textAlign: 'center' }}>
                          {qty}
                        </span>
                        <button type="button" onClick={() => setQty(m.id, 1)} disabled={atMax}
                          style={{ width: 34, height: 34, borderRadius: '50%', border: `1.5px solid ${C.border}`, background: atMax ? '#f5f5f5' : C.white, cursor: atMax ? 'not-allowed' : 'pointer', fontSize: 20, color: atMax ? C.muted : C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          +
                        </button>
                      </div>
                    </div>
                  )})}
                </div>
                {/* (Retiré : le récap « boardDesc · Caution » qui s'affichait ici dès qu'une planche
                    était choisie. Le pied de page collant dit déjà « 1 jour · 1 planche · + caution
                    200€ en empreinte », juste en dessous. Ce doublon coûtait 60 px et poussait les
                    options hors de l'écran — elles n'étaient jamais vues, donc jamais vendues.) */}

                {/* ── OPTIONS & EXTRAS ── */}
                {optionsList.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: C.muted, marginBottom: 14 }}>
                      Options & Extras
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {optionsList.map(opt => {
                        const qty = Number(form.optionQtys[opt.id]) || 0
                        return (
                          <div key={opt.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: C.white, border: `1.5px solid ${qty > 0 ? C.accent : C.border}`, borderRadius: 12 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: 15, color: C.ink }}>{opt.nom}</div>
                              {opt.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{opt.description}</div>}
                              <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginTop: 4 }}>
                                {opt.prix > 0 ? `${opt.prix} €` : 'Inclus'}{opt.unite !== 'par réservation' ? ` / ${opt.unite}` : ''}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                              <button type="button" onClick={() => setOptionQty(opt.id, -1)} disabled={qty === 0}
                                style={{ width: 32, height: 32, borderRadius: '50%', border: `1.5px solid ${C.border}`, background: qty === 0 ? '#f5f5f5' : C.white, cursor: qty === 0 ? 'not-allowed' : 'pointer', fontSize: 18, color: qty === 0 ? C.muted : C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                              <span style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 800, fontSize: 18, color: C.ink, minWidth: 20, textAlign: 'center' }}>{qty}</span>
                              <button type="button" onClick={() => setOptionQty(opt.id, 1)}
                                style={{ width: 32, height: 32, borderRadius: '50%', border: `1.5px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: 18, color: C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </motion.div>

            ) : step === 3 ? (
              <motion.div key="step3" custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.28, ease: [0.16,1,0.3,1] }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.accent, marginBottom: 10 }}>Étape {step} / {STEPS.length}</p>
                {/* ── VOS COORDONNÉES (écran dédié) ── */}
                <div>
                  <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 400, fontSize: 22, letterSpacing: '-0.5px', color: C.ink, marginBottom: 6 }}>
                    Vos coordonnées
                  </h3>
                  <p style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
                    Pour votre contrat et l’email de confirmation de réservation.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                  {/* CODE SÉJOUR — en HAUT et REPLIÉ (l'intention d'origine de showPromo, jamais branchée).
                      Il était affiché déplié tout en bas, sous « Message (optionnel) » : hors écran sur
                      mobile. Un client venu d'un camping ne le voyait pas → code non appliqué →
                      attribution du prescripteur perdue. Replié = une ligne, visible, sans inquiéter
                      les 4 clients sur 5 qui n'en ont pas. Déplié d'office si le code vient du QR. */}
                  {showPromo || form.promoCode ? (
                    <div style={{ border: `2px dashed ${C.accent}`, borderRadius: 12, padding: '14px 16px', background: 'rgba(43,76,126,0.04)' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 2 }}>Tu es en séjour près de chez nous ?</div>
                      <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 12 }}>Entre le code séjour de ton hébergement (camping, gîte…) pour le tarif spécial.</div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <input value={form.promoCode} autoFocus={showPromo && !form.promoCode}
                          onChange={e => { setPromoInfo(null); setSejourOk(false); setForm(f => ({ ...f, promoCode: e.target.value.toUpperCase() })) }}
                          placeholder="PLOUF-XXXX" style={{ ...inputStyle, fontFamily: 'monospace', borderBottom: `1.5px solid ${C.accent}` }} />
                        <button type="button" onClick={applyPromo} disabled={!form.promoCode}
                          style={{ flexShrink: 0, padding: '10px 18px', borderRadius: 8, border: 'none', background: form.promoCode ? C.accent : C.border, color: '#fff', fontSize: 13, fontWeight: 700, cursor: form.promoCode ? 'pointer' : 'default' }}>
                          Appliquer
                        </button>
                      </div>
                      {promoInfo && (
                        <p style={{ fontSize: 13, marginTop: 10, fontWeight: 600, color: promoInfo.ok ? '#2D7A4F' : '#C0392B' }}>
                          {promoInfo.ok ? '✓ Tarif séjour appliqué — tu profites du prix spécial !' : 'Code invalide ou expiré.'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <button type="button" onClick={() => setShowPromo(true)}
                      style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: C.accent, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                      🎟️ J'ai un code séjour (camping, gîte…)
                    </button>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {[['firstName','Prénom *','Marie'],['lastName','Nom *','Dupont']].map(([k,l,p]) => (
                      <div key={k}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted, marginBottom: 10 }}>{l}</label>
                        <input required value={form[k]} onChange={set(k)} placeholder={p} style={inputStyle}
                          onFocus={e => e.target.style.borderBottomColor = C.accent}
                          onBlur={e => e.target.style.borderBottomColor = C.border} />
                      </div>
                    ))}
                  </div>
                  {[
                    ['email','Email *','email','marie@exemple.fr',true],
                    ['phone','Téléphone (optionnel)','tel','06 XX XX XX XX',false],
                  ].map(([k,l,t,p,r]) => (
                    <div key={k}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted, marginBottom: 10 }}>{l}</label>
                      <input required={r} type={t} value={form[k]} onChange={set(k)} placeholder={p} style={inputStyle}
                        onFocus={e => e.target.style.borderBottomColor = C.accent}
                        onBlur={e => e.target.style.borderBottomColor = C.border} />
                    </div>
                  ))}
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted, marginBottom: 10 }}>
                      Message (optionnel)
                    </label>
                    <textarea value={form.message} onChange={set('message')} rows={3}
                      placeholder="Questions, niveau, informations particulières..."
                      style={{ ...inputStyle, resize: 'none', paddingTop: 8 }}
                      onFocus={e => e.target.style.borderBottomColor = C.accent}
                      onBlur={e => e.target.style.borderBottomColor = C.border} />
                  </div>
                  </div>
                </div>
              </motion.div>

            ) : step === 4 ? (
              <motion.div key="step4" custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.28, ease: [0.16,1,0.3,1] }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.accent, marginBottom: 10 }}>Étape {step} / {STEPS.length}</p>
                <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.8rem,4vw,2.6rem)', letterSpacing: '-1px', color: C.ink, marginBottom: 6 }}>
                  Vérifiez votre réservation
                </h2>
                <p style={{ fontSize: 14, color: C.muted, marginBottom: 28, lineHeight: 1.6 }}>
                  Tout est bon ? Il restera le contrat à signer, puis le paiement.
                </p>

                {/* Carte récap style Airbnb */}
                <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
                  {/* Semaines */}
                  <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, margin: '0 0 6px' }}>Durée</p>
                        {selectedWeeks.map(w => (
                          <p key={w.id} style={{ fontSize: 14, color: C.ink, margin: '0 0 2px' }}>
                            {w.label} · du {fullDate(w.start)} au retour {fullDate(w.end)}
                          </p>
                        ))}
                      </div>
                      <button type="button" onClick={() => goTo(1)}
                        style={{ fontSize: 12, fontWeight: 700, color: C.accent, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, flexShrink: 0 }}>
                        Modifier
                      </button>
                    </div>
                  </div>
                  {/* Planches */}
                  <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, margin: '0 0 6px' }}>Matériel inclus</p>
                        <p style={{ fontSize: 14, color: C.ink, margin: '0 0 2px' }}>{boardDesc}</p>
                        <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Pagaie, leash, pompe — tout inclus dans la location</p>
                      </div>
                      <button type="button" onClick={() => goTo(2)}
                        style={{ fontSize: 12, fontWeight: 700, color: C.accent, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                        Modifier
                      </button>
                    </div>
                  </div>
                  {/* Vous */}
                  <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, margin: '0 0 6px' }}>Contact</p>
                        <p style={{ fontSize: 14, color: C.ink, margin: '0 0 2px' }}>{form.firstName} {form.lastName}</p>
                        <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>{form.email}{form.phone ? ' · '+form.phone : ''}</p>
                      </div>
                      <button type="button" onClick={() => goTo(3)}
                        style={{ fontSize: 12, fontWeight: 700, color: C.accent, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                        Modifier
                      </button>
                    </div>
                  </div>
                  {/* Options recap */}
                  {optionItems.length > 0 && (
                    <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, margin: '0 0 6px' }}>Options</p>
                          {optionItems.map(o => (
                            <p key={o.id} style={{ fontSize: 14, color: C.ink, margin: '0 0 2px' }}>
                              {o.nom} ×{o.qty}{o.unite !== 'par réservation' ? ` (${o.unite})` : ''}{o.prix > 0 ? ` — ${o.total}€` : ' — Inclus'}
                            </p>
                          ))}
                        </div>
                        <button type="button" onClick={() => goTo(2)}
                          style={{ fontSize: 12, fontWeight: 700, color: C.accent, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, flexShrink: 0 }}>
                          Modifier
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Total */}
                  <div style={{ padding: '18px 20px', background: 'rgba(43,76,126,0.04)' }}>
                    {optionItems.length > 0 && (
                      <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.muted }}>
                          <span>Planches ({boardDesc})</span><span>{totalPrice}€</span>
                        </div>
                        {optionItems.map(o => (
                          <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.muted }}>
                            <span>{o.nom} ×{o.qty}{o.unite !== 'par réservation' ? ` · ${o.unite}` : ''}</span><span>{o.prix > 0 ? `${o.total}€` : 'Inclus'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, margin: '0 0 4px' }}>Estimation totale</p>
                        <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>+ caution {cautionPerBoard*totalBoards}€ (restituée)</p>
                      </div>
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 800, fontSize: 26, color: C.ink, margin: 0 }}>
                        {grandTotal}€
                      </p>
                    </div>
                  </div>
                </div>

                {error && (
                  <p style={{ fontSize: 13, color: '#C0392B', padding: '10px 14px', background: '#FEF2F2', borderRadius: 6, marginBottom: 8 }}>{error}</p>
                )}
              </motion.div>

            ) : step === 5 ? (
              // ── CONTRAT & SIGNATURE — écran dédié ──
              // Il était collé sous le récap : sur un VRAI iPhone (zone web 390×664, pas 844),
              // le récap remplit l'écran et le titre du contrat tombait 148 px plus bas. Laurent
              // ne voyait même pas qu'il y avait un contrat à signer. Un écran, une chose.
              <motion.div key="step5" custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.28, ease: [0.16,1,0.3,1] }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.accent, marginBottom: 10 }}>Étape {step} / {STEPS.length}</p>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 400, fontSize: 22, letterSpacing: '-0.5px', color: C.ink, marginBottom: 6 }}>
                  Votre contrat de location
                </h3>
                <p style={{ fontSize: 14, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
                  Lisez et signez le contrat avant de régler. Vous recevrez une copie signée par email.
                </p>

                {/* Conditions */}
                <div style={{ maxHeight: 240, overflowY: 'auto', fontSize: 13, lineHeight: 1.7, color: '#475569', border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 14, background: '#fff' }}>
                  {CONTRACT_ARTICLES.split('\n\n').map((art, i) => (
                    <div key={i} style={{ marginBottom: 12 }}>
                      {art.split('\n').map((line, j) => (
                        <p key={j} style={{ margin: 0, fontWeight: j === 0 ? 700 : 400, color: j === 0 ? C.ink : '#475569' }}>{line}</p>
                      ))}
                    </div>
                  ))}
                </div>

                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16, cursor: 'pointer', fontSize: 13, color: C.ink }}>
                  <input type="checkbox" checked={contractOk} onChange={e => setContractOk(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16 }} />
                  <span>J'ai lu et j'accepte les conditions de location et les{' '}
                    <a href="/cgv" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.accent, textDecoration: 'underline' }}>CGV</a>.
                    Je reconnais que ma réservation, portant sur une activité de loisirs à date déterminée, est <strong>ferme et n'ouvre pas droit au délai de rétractation de 14 jours</strong>. Je certifie savoir nager et avoir pris connaissance des consignes de sécurité, dont la côte sauvage fortement déconseillée.</span>
                </label>

                <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 10, padding: 14, background: '#fff' }}>
                  <MiniSignaturePad onChange={setContractSig} />
                </div>

                {error && (
                  <p style={{ fontSize: 13, color: '#C0392B', padding: '10px 14px', background: '#FEF2F2', borderRadius: 6, marginTop: 12 }}>{error}</p>
                )}
              </motion.div>

            ) : step === 6 ? (
              <motion.div key="step6" custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.28, ease: [0.16,1,0.3,1] }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.accent, marginBottom: 10 }}>Étape {step} / {STEPS.length}</p>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 400, fontSize: 22, letterSpacing: '-0.5px', color: C.ink, marginBottom: 6 }}>
                  Paiement sécurisé
                </h3>
                <p style={{ fontSize: 14, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
                  Réglez la location en ligne. La caution n'est PAS débitée : elle sera simplement bloquée sur votre carte au retrait de la planche, puis restituée au retour si tout est en ordre.
                </p>

                {/* Résumé financier */}
                <div style={{ background: C.lin, borderRadius: 10, padding: '12px 16px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: C.ink }}>
                    <span>Planches ({boardDesc} · {dureeLabel(form.dureeJours, formules)})</span>
                    <span style={{ fontWeight: 700 }}>{totalPrice}€</span>
                  </div>
                  {optionItems.map(o => (
                    <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.muted }}>
                      <span>{o.nom} ×{o.qty}</span>
                      <span>{o.prix > 0 ? `${o.total}€` : 'Inclus'}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: C.muted }}>
                    <span>Caution (bloquée au retrait)</span>
                    <span>{cautionPerBoard * totalBoards}€</span>
                  </div>
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>Débité maintenant</span>
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 800, fontSize: 20, color: C.accent }}>{grandTotal}€</span>
                  </div>
                </div>

                {/* Consentement caution — obligatoire DSP2 */}
                <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <p style={{ fontSize: 11.5, color: C.muted, margin: 0, lineHeight: 1.5 }}>
                    En payant, vous autorisez le débit de <strong style={{ color: C.ink }}>{totalPrice}€</strong> (location)
                    et autorisez le <strong style={{ color: C.ink }}>blocage de {cautionPerBoard * totalBoards}€</strong> de caution, effectué au retrait de la planche —
                    <strong style={{ color: C.ink }}> jamais débitée</strong>, restituée au retour du matériel en bon état.
                  </p>
                </div>

                {/* Formulaire Stripe — verrouillé tant que le contrat n'est pas accepté ET signé.
                    Sans ce garde-fou le client paie en faisant défiler par-dessus le contrat. */}
                {!contractReady ? (
                  <div data-testid="paiement-verrouille" style={{ border: `1.5px dashed ${C.border}`, borderRadius: 10, padding: '24px 18px', textAlign: 'center', background: '#FAFBFC' }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: C.ink, margin: '0 0 6px' }}>
                      Signez le contrat pour débloquer le paiement
                    </p>
                    <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.6 }}>
                      {!contractOk && !contractSig ? 'Il reste à accepter les conditions et à signer ci-dessus.'
                        : !contractOk ? 'Il reste à cocher « J’ai lu et j’accepte les conditions » ci-dessus.'
                        : 'Il reste à signer dans le cadre ci-dessus.'}
                    </p>
                  </div>
                ) : stripeSecrets ? (
                <Elements stripe={getStripe()} options={{ clientSecret: stripeSecrets.rentalClientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: C.accent, borderRadius: '8px', fontFamily: 'DM Sans, sans-serif' } } }}>
                  <StripePaymentForm
                    stripeSecrets={stripeSecrets}
                    form={form}
                    contract={{
                      id: stripeSecrets.contractId, status: 'paid', mode_paiement: 'stripe',
                      firstName: form.firstName, lastName: form.lastName,
                      email: form.email, phone: form.phone,
                      weekId: '',
                      startDate: selectedWeeks[0]?.start, endDate: selectedWeeks[selectedWeeks.length-1]?.end,
                      price: grandTotal, caution: cautionPerBoard * totalBoards,
                      boardSize: boardDesc, message: form.message,
                      createdAt: new Date().toISOString(), boardName: boardDesc,
                      signingToken,
                      signatureContrat: contractSig, signature: contractSig, contratSigne: true,
                    }}
                    onSuccess={() => setSent(true)}
                    onError={setError}
                  />
                </Elements>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: C.muted, fontSize: 14 }}>
                    Préparation du paiement sécurisé…
                  </div>
                )}

                <p style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginTop: 16 }}>
                  Paiement sécurisé par Stripe · 3D Secure · Vos données ne transitent pas par nos serveurs
                </p>
              </motion.div>

            ) : null}
          </AnimatePresence>

          {/* Filet de sécurité : un client bloqué (bug navigateur, carte capricieuse…) a
              toujours un recours immédiat → aucune vente perdue. Visible sous chaque étape. */}
          {!sent && (
            <p style={{ textAlign: 'center', marginTop: 28, fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>
              Un souci pour réserver ?{' '}
              <a href="https://wa.me/33767451718?text=Bonjour%2C%20j'ai%20un%20souci%20pour%20r%C3%A9server%20sur%20Plouf%20%26%20Go"
                target="_blank" rel="noreferrer"
                style={{ color: C.accent || '#2B4C7E', fontWeight: 700, textDecoration: 'underline' }}>
                Écris-nous sur WhatsApp
              </a>{' '}ou appelle le{' '}
              <a href="tel:+33767451718" style={{ color: C.accent || '#2B4C7E', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>07&nbsp;67&nbsp;45&nbsp;17&nbsp;18</a>.
            </p>
          )}
        </div>
      </div>

      {/* ── FOOTER STICKY ── */}
      {!sent && step < 6 && (
        <div style={{
          flexShrink: 0,
          background: C.white, borderTop: `1px solid ${C.border}`,
          padding: '16px 24px calc(16px + env(safe-area-inset-bottom, 0px))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          {/* Prix courant */}
          <div>
            {totalBoards > 0 && selectedWeeks.length > 0 ? (
              <>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 800, fontSize: 20, color: C.ink, margin: 0 }}>
                  {grandTotal}€
                </p>
                <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>{form.dureeJours} jour{form.dureeJours > 1 ? 's' : ''} · {totalBoards} planche{totalBoards > 1 ? 's' : ''} · + caution {cautionPerBoard*totalBoards}€ en empreinte</p>
              </>
            ) : selectedWeeks.length > 0 ? (
              // Créneaux choisis mais 0 planche : on n'affiche JAMAIS « 0 € » → prix par planche + invite.
              <>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 800, fontSize: 20, color: C.ink, margin: 0 }}>
                  {prixParPlanche}€ <span style={{ fontSize: 13, fontWeight: 500, color: C.muted }}>/ planche</span>
                </p>
                <p style={{ fontSize: 11, color: C.accent, fontWeight: 600, margin: 0 }}>Choisissez vos planches ↑</p>
              </>
            ) : (
              <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Paiement sécurisé par Stripe</p>
            )}
          </div>

          {/* Navigation */}
          <div style={{ display: 'flex', gap: 10 }}>
            {step > 1 && (
              <button onClick={back}
                style={{ padding: '14px 24px', borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white, fontSize: 14, fontWeight: 600, color: C.ink, cursor: 'pointer' }}>
                ← Retour
              </button>
            )}
            {step < 5 ? (
              <button onClick={next} disabled={!canNext()}
                style={{
                  padding: '14px 32px', borderRadius: 8, border: 'none',
                  background: canNext() ? C.ink : C.border,
                  fontSize: 14, fontWeight: 700, color: '#fff', cursor: canNext() ? 'pointer' : 'default',
                  transition: 'background 0.15s',
                }}>
                Continuer →
              </button>
            ) : step === 5 ? (
              // Le paiement n'est préparé qu'ICI, après signature : plus de PaymentIntent
              // créé chez Stripe pour chaque curieux qui abandonne avant de signer.
              <motion.button onClick={goToPayment} disabled={sending || !canNext()}
                whileHover={{ opacity: 0.88 }} whileTap={{ scale: 0.98 }}
                style={{ padding: '14px 32px', borderRadius: 8, border: 'none', background: (sending || !canNext()) ? C.border : C.accent, fontSize: 14, fontWeight: 700, color: '#fff', cursor: (sending || !canNext()) ? 'default' : 'pointer' }}>
                {sending ? 'Préparation…' : 'Vers le paiement →'}
              </motion.button>
            ) : null}
          </div>
        </div>
      )}
    </motion.div>
  )
}

/* ────────── WEEK PICKER ────────── */
function WeekPicker({ openModal, title, initialPromo }) {
  const cautionPerBoard = useCautionPerBoard()
  const [promoCode, setPromoCode] = useState('')
  const [sejourOk, setSejourOk] = useState(false)
  const { formules, saison, prixJourSupp } = useReservationModel()

  // Étape 1 « inline » : la durée + le jour se choisissent DIRECTEMENT dans cette rubrique.
  const [duree, setDuree] = useState(4)   // sert au mini-lanceur « Plus de 3 jours ? » (les 1/2/3 j lancent via leur carte)
  const [startDate, setStartDate] = useState('')
  const [dateChoice, setDateChoice] = useState(null) // 'today' | 'tomorrow' | 'cal'
  const [dateErr, setDateErr] = useState('')
  const _now = new Date()
  const todayISO = new Date(Date.UTC(_now.getFullYear(), _now.getMonth(), _now.getDate())).toISOString().slice(0, 10)

  // Code séjour porté par un flyer (?sejour=CODE) → pré-appliqué (le tarif séjour s'affiche).
  useEffect(() => {
    if (initialPromo?.code && initialPromo?.sejourOk) { setPromoCode(initialPromo.code); setSejourOk(true) }
  }, [initialPromo?.code, initialPromo?.sejourOk])

  // Si la durée augmente et fait dépasser la fin de saison, on efface la date choisie.
  useEffect(() => {
    if (startDate && !departValide(startDate, duree, saison)) { setStartDate(''); setDateChoice(null) }
  }, [duree]) // eslint-disable-line react-hooks/exhaustive-deps

  const pickStart = (iso, choice) => {
    if (!iso) return
    if (!departValide(iso, duree, saison)) { setDateErr('Cette date ne permet pas de terminer la location avant la fin de saison.'); return }
    setDateErr(''); setDateChoice(choice); setStartDate(iso)
  }

  const fmtJour = (v) => (Math.round(v * 100) / 100).toFixed(2).replace('.', ',').replace(/,00$/, '')
  const tarif = sejourOk ? 'sejour' : 'public'
  const prixParPlanche = prixFormule(duree, tarif, { formules, prixJourSupp })
  const refJour = duree * prixFormule(1, tarif, { formules, prixJourSupp })
  const pct = refJour > 0 ? Math.round((refJour - prixParPlanche) / refJour * 100) : 0
  const kickStyle = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5E7683', margin: '22px 0 12px' }
  const qkStyle = (on) => ({ border: `1.5px solid ${on ? '#0E4D6B' : '#DCEAF1'}`, background: on ? '#0E4D6B' : '#fff', color: on ? '#fff' : '#0E4D6B', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 14, padding: '12px 18px', borderRadius: 12, cursor: 'pointer' })

  // Saison fermée = interrupteur manuel (saison.closed) OU date dépassée (aujourd'hui >= dernier retour).
  // Réversible : mettre closed:false + des dates 2027 dans app_config.saison rouvre le tunnel à l'identique.
  const saisonFermee = saison?.closed === true || (!!saison?.lastReturn && todayISO >= saison.lastReturn)

  if (saisonFermee) {
    return (
      <section id="semaines" style={{ background: '#F2F5F7', padding: '96px 24px' }}>
        <div style={{ maxWidth: 620, margin: '0 auto', textAlign: 'center', background: '#fff', border: '1.5px solid #DCEAF1', borderRadius: 22, padding: 'clamp(28px, 5vw, 48px)', boxShadow: '0 20px 50px rgba(0,40,70,0.06)' }}>
          <div style={{ fontSize: 46, marginBottom: 10 }}>🌊</div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 400, fontSize: 'clamp(1.8rem, 4.5vw, 2.6rem)', color: '#0E4D6B', margin: '0 0 14px', lineHeight: 1.05 }}>Saison 2026 terminée</h2>
          <p style={{ fontSize: 16, color: '#5E7683', lineHeight: 1.65, margin: '0 0 26px' }}>
            Merci à toutes et à tous pour cet été ! 🏄<br />
            Les réservations en ligne sont fermées. On se retrouve l'été prochain pour de nouveaux ploufs.
          </p>
          <a href="https://wa.me/33767451718?text=Bonjour%2C%20une%20question%20sur%20Plouf%20%26%20Go" target="_blank" rel="noreferrer"
            style={{ display: 'inline-block', padding: '14px 26px', borderRadius: 12, background: '#0E4D6B', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 15 }}>
            Nous écrire sur WhatsApp
          </a>
          <div style={{ fontSize: 13.5, color: '#94A3B8', marginTop: 16 }}>Une question ? <b style={{ color: '#5E7683' }}>07&nbsp;67&nbsp;45&nbsp;17&nbsp;18</b> · contact@ploufandgo.com</div>
        </div>
      </section>
    )
  }

  return (
    <section id="semaines" style={{ background: '#F2F5F7', padding: '96px 24px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* En-tête */}
        <motion.div {...fadeUp(0)}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#5BC8E8', filter: 'brightness(0.8)', marginBottom: 12 }}>Réserve ta planche</p>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 400, fontSize: 'clamp(2rem, 5vw, 3rem)', letterSpacing: '0.4px', color: '#0E4D6B', margin: '0 0 10px', lineHeight: 1 }}>{title || 'Le prix du plouf'}</h2>
          <p style={{ fontSize: 16, color: '#5E7683', margin: 0, lineHeight: 1.5 }}>Touche une formule et on t'emmène pas à pas : date, planche, paiement — tout est guidé. Pagaie, pompe, leash, gilet : tout est dedans.</p>
        </motion.div>

        {/* Carte « étape 1 » — durée + date directement ici */}
        <motion.div {...fadeUp(0.05)} style={{ background: '#fff', border: '1.5px solid #DCEAF1', borderRadius: 22, padding: 'clamp(20px, 4vw, 32px)', marginTop: 28, boxShadow: '0 20px 50px rgba(0,40,70,0.06)' }}>
          {sejourOk && (
            <div style={{ background: 'rgba(45,122,79,0.07)', border: '1.5px solid #2D7A4F', borderRadius: 12, padding: '10px 14px', marginBottom: 16, fontSize: 13, fontWeight: 700, color: '#2D7A4F' }}>Tarif séjour appliqué ✓</div>
          )}

          {/* DURÉE — liste compacte (design maquette validée) : TOUCHER une ligne lance la résa */}
          <p style={{ ...kickStyle, marginTop: 0 }}>Tu la gardes combien de temps ?</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {DUREE_PRESETS.map(p => {
              const pub = prixFormule(p.duree, 'public', { formules, prixJourSupp })
              const sej = prixFormule(p.duree, 'sejour', { formules, prixJourSupp })
              const isPhare = p.key === 'journee'
              const perDay = fmtJour((sejourOk ? sej : pub) / p.duree)
              const label = p.duree === 1 ? 'Journée' : `${p.duree} jours`
              const sub = p.duree === 1 ? 'à rendre le soir même' : 'gardée jour et nuit'
              return (
                <motion.button key={p.key} type="button" onClick={() => openModal('', promoCode, sejourOk, p.duree, '', 1)}
                  whileTap={{ scale: 0.99 }} whileHover={{ borderColor: '#0E4D6B' }}
                  style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer',
                    borderRadius: 15, padding: '15px 16px', background: '#fff', color: '#0E4D6B', transition: 'all 0.15s',
                    border: `2px solid ${isPhare ? '#F5C142' : '#DCEAF1'}` }}>
                  {isPhare && <span style={{ position: 'absolute', top: -10, left: 14, background: '#F5C142', color: '#0E4D6B', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 9px', borderRadius: 999 }}>Le + simple</span>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 800 }}>{label}</div>
                    <div style={{ fontSize: 12, color: '#5E7683', marginTop: 1 }}>{sub}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {sejourOk ? (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, justifyContent: 'flex-end' }}>
                        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 22, fontWeight: 800 }}>{sej} €</span>
                        <span style={{ fontSize: 12, textDecoration: 'line-through', opacity: 0.6 }}>{pub} €</span>
                      </div>
                    ) : (<div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 22, fontWeight: 800 }}>{pub} €</div>)}
                    <div style={{ fontSize: 10.5, color: '#5E7683' }}>≈ {perDay} €/j</div>
                  </div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: isPhare ? '#F5C142' : '#0E4D6B', color: isPhare ? '#0E4D6B' : '#fff', fontSize: 12.5, fontWeight: 800, padding: '8px 13px', borderRadius: 10, marginLeft: 2, whiteSpace: 'nowrap' }}>Réserver →</span>
                </motion.button>
              )
            })}
          </div>

          {/* Envie de plus (au-delà de 3 j) — visible juste sous les formules */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 10, padding: '12px 14px', borderRadius: 15, border: '1.5px dashed #C6D7E1', background: '#F7FAFC' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0B2A3A' }}>Envie de plus ? · {duree} jours</div>
              <div style={{ fontSize: 11.5, color: '#5E7683' }}>{prixFormule(duree, tarif, { formules, prixJourSupp })} € / planche · +{prixJourSupp?.['prix_' + tarif] ?? 0} €/jour</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" onClick={() => setDuree(d => Math.max(4, d - 1))} disabled={duree <= 4} style={{ width: 30, height: 30, borderRadius: '50%', border: '1.5px solid #DCEAF1', background: duree <= 4 ? '#f5f5f5' : '#fff', cursor: duree <= 4 ? 'not-allowed' : 'pointer', fontSize: 17, color: '#0E4D6B' }}>−</button>
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 800, fontSize: 17, minWidth: 20, textAlign: 'center' }}>{duree}</span>
              <button type="button" onClick={() => setDuree(d => d + 1)} style={{ width: 30, height: 30, borderRadius: '50%', border: '1.5px solid #DCEAF1', background: '#fff', cursor: 'pointer', fontSize: 17, color: '#0E4D6B' }}>+</button>
              <button type="button" onClick={() => openModal('', promoCode, sejourOk, duree, '', 1)} style={{ marginLeft: 4, padding: '9px 14px', borderRadius: 10, border: 'none', background: '#0E4D6B', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Réserver {duree} j →</button>
            </div>
          </div>

          {/* Note caution */}
          <p style={{ fontSize: 12.5, color: '#5E7683', marginTop: 12, lineHeight: 1.5 }}>
            Caution {cautionPerBoard} € en empreinte, jamais débitée. Pagaie, pompe, leash, gilet inclus.
          </p>
        </motion.div>

      </div>
    </section>
  )
}

/* ────────── PAGE PRINCIPALE ────────── */
export default function Landing() {
  const modelesList = useModeles()
  const { formules, saison } = useReservationModel()
  // Saison fermée (même logique que WeekPicker) → tous les boutons « Réserver » renvoient au message.
  const _nowL = new Date()
  const todayISOL = new Date(Date.UTC(_nowL.getFullYear(), _nowL.getMonth(), _nowL.getDate())).toISOString().slice(0, 10)
  const saisonFermee = saison?.closed === true || (!!saison?.lastReturn && todayISOL >= saison.lastReturn)
  // Affichage OPTIMISTE : si l'URL contient ?sejour=CODE, on montre l'écran séjour dès le 1er rendu
  // (sans attendre la validation serveur) → plus de flash "accueil classique → page promo".
  // La validation ci-dessous confirme le nom du camping / retire l'offre si le code est faux.
  const [partner, setPartner] = useState(() => {
    try { const c = new URLSearchParams(window.location.search).get('sejour'); return c ? { code: c, nom: null, type: null, pending: true } : null } catch { return null }
  })
  const [scrolled, setScrolled] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalPromo, setModalPromo] = useState({ code: '', sejourOk: false })
  const [modalBoardSize, setModalBoardSize] = useState('')
  const [modalDuree, setModalDuree] = useState(1)  // durée pré-sélectionnée (rubrique tarifs)
  const [modalStartDate, setModalStartDate] = useState('') // date pré-remplie (rubrique tarifs)
  const [modalStep, setModalStep] = useState(1)    // étape de départ (2 si l'étape 1 a été faite dans la rubrique)
  const [menuOpen, setMenuOpen] = useState(false)
  const [weekPickerTitle, setWeekPickerTitle] = useState('')
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [threeDS, setThreeDS] = useState(null)          // null | 'finalizing' | 'done' | 'failed'
  const [threeDSContract, setThreeDSContract] = useState(null)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn, { passive: true })
    return () => window.removeEventListener('resize', fn)
  }, [])

  // ── Retour de 3D Secure avec REDIRECTION ──
  // La carte a redirigé le client hors du site puis l'a ramené ici (return_url) avec
  // ?payment_intent=...&redirect_status=... → on finalise la réservation (l'état du
  // tunnel a été perdu au rechargement ; on le récupère depuis localStorage).
  useEffect(() => {
    // Les paramètres de retour arrivent normalement dans ?search. Filet : si un ancien
    // return_url portait une ancre (#semaines), Stripe a pu les coller APRÈS le # → on lit
    // alors la portion après '?' du hash. On couvre les deux cas.
    const hashQuery = window.location.hash.includes('?') ? window.location.hash.split('?').slice(1).join('?') : ''
    const params = new URLSearchParams(window.location.search || (hashQuery ? '?' + hashQuery : ''))
    const redirectStatus = params.get('redirect_status')
    const piId = params.get('payment_intent')
    if (!redirectStatus || !piId) return
    window.history.replaceState({}, '', window.location.pathname) // nettoie l'URL

    let pending = null
    try { pending = JSON.parse(localStorage.getItem('paddle_pending_3ds') || 'null') } catch {}

    if (redirectStatus !== 'succeeded') { setThreeDS('failed'); return }
    setThreeDS('finalizing')
    ;(async () => {
      const paymentIntentId = (pending && pending.paymentIntentId) || piId
      const signature = pending?.signature || null
      try {
        await fetch('/.netlify/functions/confirm-reservation', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId, signature }),
        })
        if (pending) {
          try {
            const { data: ownerSig } = await supabase.rpc('get_owner_signature')
            const { generateContractPDF } = await import('../services/pdfGenerator')
            const d = pending.display || {}
            const doc = generateContractPDF(
              { id: pending.contractId, firstName: d.firstName, lastName: d.lastName, email: d.email,
                boardModel: d.boardSize, price: d.price, caution: d.caution,
                startDate: d.startDate, endDate: d.endDate, signature },
              { ...DEFAULT_SETTINGS, ownerSignature: ownerSig || '' }
            )
            const pdfBase64 = doc.output('datauristring').split(',')[1]
            await fetch('/.netlify/functions/confirm-signature', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: pending.contractId, token: pending.signingToken, pdfBase64 }),
            })
          } catch {}
        }
      } catch {
        // Même si ceci échoue, le webhook Stripe crée la résa → on rassure le client.
      } finally {
        setThreeDSContract(pending?.display || null)
        setThreeDS('done')
        try { localStorage.removeItem('paddle_pending_3ds') } catch {}
      }
    })()
  }, [])

  // Arrivée depuis un flyer partenaire : ?sejour=ROHU → on valide le code et on récupère
  // le nom du partenaire pour l'accueil personnalisé (le pré-remplissage du tarif passe par WeekPicker).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('sejour')
    if (!code) return
    // Nettoie l'URL tout de suite : on retire ?sejour (le lien codé ne traîne pas dans la barre,
    // ne se partage/s'indexe pas) tout en gardant les autres paramètres (ex. ?modele).
    params.delete('sejour')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''))
    fetch('/.netlify/functions/validate-promo', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
    }).then(r => r.json()).then(d => {
      if (d.ok && d.type === 'sejour') setPartner({ code: d.code, nom: d.partenaire?.nom || null, type: d.partenaire?.type || null })
      else setPartner(null)   // code explicitement invalide → on retire l'écran séjour (retour accueil)
    }).catch(() => {})        // erreur réseau : on GARDE l'affichage optimiste (le tarif est revalidé au paiement)
  }, [])

  const scrollToWeeks = () => {
    // Le titre de la rubrique est fixe (« Le prix du plouf ») — on ne fait que scroller.
    setTimeout(() => {
      const el = document.getElementById('semaines')
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 60
        window.scrollTo({ top, behavior: 'smooth' })
      }
    }, 100)
  }

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])

  // Ouvre le tunnel. Pré-sélection éventuelle d'un modèle de planche (depuis une fiche)
  // + code séjour porté par un flyer partenaire. La date/durée se choisissent à l'étape 1.
  const openModal = (boardSize = '', promoCode = '', sejourOk = false, duree = 1, startDate = '', step = 1) => {
    // Saison fermée : on n'ouvre pas le tunnel, on renvoie vers le message « Saison terminée ».
    if (saisonFermee) { try { document.getElementById('semaines')?.scrollIntoView({ behavior: 'smooth' }) } catch { /* noop */ } return }
    setModalPromo({ code: promoCode || '', sejourOk: !!sejourOk })
    setModalBoardSize(boardSize)
    setModalDuree(duree > 0 ? duree : 1)
    setModalStartDate(startDate || '')
    setModalStep(step > 1 ? step : 1)
    setModalOpen(true)
  }

  /* ── NAV ── */
  const navLinks = [['#semaines','Semaines'],['#planches','Planches'],['#comment','Comment ça marche'],['#contact','Contact']]

  return (
    <div style={{ background: C.bg, color: C.ink, fontFamily: '"DM Sans", sans-serif', minHeight: '100vh' }}>

      {/* ── ACCUEIL PARTENAIRE (plein écran, orienté action) — arrivée via flyer ?sejour=CODE. Additif. ── */}
      {partner && (
        <section style={{ position: 'relative', minHeight: '100svh', background: '#0E4D6B', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '100px 24px 64px', boxSizing: 'border-box', overflow: 'hidden' }}>
          <img src="/hero_paddles.jpg" aria-hidden="true" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%', opacity: 0.22 }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(14,77,107,0.86), rgba(14,77,107,0.72))' }} />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: 620 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#5BC8E8', marginBottom: 18 }}>
              Tarif séjour{partner.nom ? <> · {partner.nom}</> : ''}
            </div>
            <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 'clamp(2.4rem,7vw,4.2rem)', lineHeight: 1.02, letterSpacing: '-1px', textTransform: 'uppercase', margin: '0 0 16px' }}>
              Bienvenue&nbsp;!
            </h1>
            <p style={{ fontSize: 'clamp(0.95rem,2.2vw,1.15rem)', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, margin: '0 0 28px' }}>
              T'es à deux pas. Ta planche t'attend — à la journée, 2 ou 3 jours, tout inclus.<br/>Ton prix est déjà débloqué, rien à saisir.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 32, flexWrap: 'wrap' }}>
              {['journee', 'deux_jours', 'trois_jours'].map(k => {
                const f = formules?.[k]; if (!f) return null
                return (
                  <div key={k} style={{ textAlign: 'center' }}>
                    <div>
                      <span style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through' }}>{f.prix_public}€</span>{' '}
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 800, fontSize: 32, color: k === 'trois_jours' ? '#F5C142' : '#5BC8E8' }}>{f.prix_sejour}€</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>{f.label}</div>
                  </div>
                )
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              <motion.button onClick={() => scrollToWeeks('Choisissez votre formule')} whileHover={{ opacity: 0.9 }} whileTap={{ scale: 0.97 }}
                style={{ background: '#5BC8E8', color: '#0E4D6B', fontWeight: 800, fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '16px 34px', borderRadius: 8, border: 'none', cursor: 'pointer' }}>
                Réserver au tarif séjour →
              </motion.button>
              <motion.button onClick={() => { const el = document.getElementById('concept'); if (el) el.scrollIntoView({ behavior: 'smooth' }) }} whileHover={{ opacity: 0.9 }} whileTap={{ scale: 0.97 }}
                style={{ background: 'transparent', color: '#fff', fontWeight: 700, fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '16px 34px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                Découvrir Plouf &amp; Go
              </motion.button>
            </div>
            <div style={{ marginTop: 16, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>code {partner.code} appliqué ✓</div>
          </div>
        </section>
      )}

      {/* RETOUR 3D SECURE — overlay de finalisation/confirmation */}
      {threeDS && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ maxWidth: 440, textAlign: 'center' }}>
            {threeDS === 'finalizing' && (
              <>
                <div style={{ fontSize: 40, marginBottom: 14 }}>⏳</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.ink }}>Finalisation de votre réservation…</div>
                <div style={{ fontSize: 14, color: C.muted, marginTop: 8 }}>Merci de patienter quelques secondes.</div>
              </>
            )}
            {threeDS === 'done' && (
              <>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🏄</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 8 }}>
                  Réservation confirmée{threeDSContract?.firstName ? `, ${threeDSContract.firstName}` : ''} !
                </div>
                <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, marginBottom: 20 }}>
                  Votre paiement a bien été validé{threeDSContract?.email ? ` — un email de confirmation a été envoyé à ${threeDSContract.email}` : ''}.
                  {threeDSContract?.boardSize ? ` (${threeDSContract.boardSize})` : ''} Rendez-vous au 17 Rue Docteur Le Gall, Saint-Pierre-Quiberon.
                </div>
                <button onClick={() => setThreeDS(null)} style={{ padding: '12px 28px', borderRadius: 8, background: C.accent || C.primary, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                  Continuer
                </button>
              </>
            )}
            {threeDS === 'failed' && (
              <>
                <div style={{ fontSize: 44, marginBottom: 12 }}>❌</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#B71C1C', marginBottom: 8 }}>Paiement non abouti</div>
                <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, marginBottom: 20 }}>
                  Votre carte n'a pas validé l'authentification. Aucun montant n'a été débité. Vous pouvez réessayer.
                </div>
                <button onClick={() => setThreeDS(null)} style={{ padding: '12px 28px', borderRadius: 8, background: C.accent || C.primary, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                  Réessayer
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* MODAL */}
      <AnimatePresence>
        {modalOpen && (
          <ReservationModal onClose={() => setModalOpen(false)} initialBoardSize={modalBoardSize} initialPromoCode={modalPromo.code} initialSejourOk={modalPromo.sejourOk} initialDuree={modalDuree} initialStartDate={modalStartDate} initialStep={modalStep} />
        )}
      </AnimatePresence>

      {/* ── NAV ── */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        transition: 'all 0.35s ease',
        background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? `1px solid ${C.border}` : 'none',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 32px', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="#" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <img
              src="/plouf_go_logo_profil_carre.png"
              alt="Plouf and Go"
              style={{
                height: 88, width: 'auto', objectFit: 'contain',
                borderRadius: 10,
                /* Quand fond blanc : on ajoute un léger fond marine derrière le logo */
                background: scrolled ? '#0E4D6B' : 'transparent',
                padding: scrolled ? '4px' : 0,
                transition: 'all 0.35s ease',
              }}
            />
          </a>
          <button onClick={() => setMenuOpen(m => !m)} aria-label="Menu"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ display: 'block', width: 20, height: 1.5, background: scrolled ? C.ink : '#fff', transition: 'all 0.3s', transform: menuOpen ? 'rotate(45deg) translate(4.5px,4.5px)' : 'none' }} />
            <span style={{ display: 'block', width: 20, height: 1.5, background: scrolled ? C.ink : '#fff', transition: 'all 0.3s', opacity: menuOpen ? 0 : 1 }} />
            <span style={{ display: 'block', width: 20, height: 1.5, background: scrolled ? C.ink : '#fff', transition: 'all 0.3s', transform: menuOpen ? 'rotate(-45deg) translate(4.5px,-4.5px)' : 'none' }} />
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              style={{ background: C.bg, borderTop: `1px solid ${C.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '24px 32px 32px', display: 'flex', flexDirection: 'column', gap: 22 }}>
                {navLinks.map(([h,l]) => (
                  <a key={h} href={h} onClick={() => setMenuOpen(false)}
                    style={{ fontSize: 16, fontWeight: 400, color: C.muted, textDecoration: 'none' }}>{l}</a>
                ))}
                <button onClick={() => { openModal(); setMenuOpen(false) }}
                  style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '14px 24px', background: C.ink, color: '#fff', border: 'none', cursor: 'pointer', borderRadius: 2, marginTop: 8 }}>
                  RÉSERVER MAINTENANT
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── HERO — charte Plouf & Go ── */}
      <section style={{
        position: 'relative', minHeight: '100svh', overflow: 'hidden',
        background: '#0E4D6B', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center',
        padding: '80px clamp(24px,6vw,72px) 80px',
      }}>
        {/* Photo fond + double overlay : assombrit + teinte marine pour garder la charte */}
        <img src="/hero_paddles.jpg" aria-hidden="true" alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 40%',
            filter: 'saturate(0.8)',
          }} />
        {/* Overlay marine semi-transparent pour lisibilité */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(14,77,107,0.82) 0%, rgba(14,77,107,0.65) 60%, rgba(14,77,107,0.5) 100%)' }} />

        {/* ── Layout flex : texte gauche + illustration droite ── */}
        <div style={{
          width: '100%', maxWidth: 1200, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 'clamp(16px,4vw,48px)',
          flexWrap: 'wrap',         /* bascule en colonne sur mobile */
        }}>

          {/* GAUCHE — slogan + localisation + bouton */}
          <div style={{ flex: '1 1 280px', zIndex: 1 }}>
            <motion.p
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15, ease }}
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, fontWeight: 600,
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.5)', margin: '0 0 20px' }}>
              Location Paddle · Saint-Pierre-Quiberon · Été 2026
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease }}
              style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800,
                fontSize: 'clamp(2.8rem, 6vw, 6rem)', lineHeight: 1.0,
                letterSpacing: '-1px', margin: '0 0 40px', textTransform: 'uppercase' }}>
              <span style={{ color: '#fff',    display: 'block' }}>Tu loues.</span>
              <span style={{ color: '#5BC8E8', display: 'block' }}>Tu pars.</span>
              <span style={{ color: '#F5C142', display: 'block' }}>Tu plouf.</span>
            </motion.h1>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease }}
              style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <motion.button
                onClick={() => { const el = document.getElementById('concept'); if (el) el.scrollIntoView({ behavior: 'smooth' }) }}
                whileHover={{ y: 3 }} whileTap={{ scale: 0.97 }}
                style={{
                  background: 'none', border: '1.5px solid rgba(255,255,255,0.4)',
                  color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
                  textTransform: 'uppercase', padding: '13px 36px', borderRadius: 2,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 10,
                  fontFamily: 'DM Sans, sans-serif',
                }}>
                Découvrir
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <line x1="7" y1="1" x2="7" y2="12" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M3 8.5l4 4 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.button>
              <motion.button
                onClick={() => scrollToWeeks('Choisissez votre formule')}
                whileHover={{ opacity: 0.9 }} whileTap={{ scale: 0.97 }}
                style={{
                  background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.7)',
                  color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
                  textTransform: 'uppercase', padding: '13px 36px', borderRadius: 2,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 10,
                  fontFamily: 'DM Sans, sans-serif', backdropFilter: 'blur(6px)',
                }}>
                Voir les disponibilités →
              </motion.button>
            </motion.div>
          </div>

        </div>

        {/* Vague cyan collée en bas */}
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none"
          style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 'clamp(48px,7vw,80px)' }}>
          <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,20 1440,35 L1440,80 L0,80 Z" fill="#5BC8E8" opacity="0.30"/>
          <path d="M0,55 C240,25 600,75 960,45 C1150,30 1320,60 1440,50 L1440,80 L0,80 Z" fill="#5BC8E8" opacity="0.18"/>
        </svg>
      </section>

      {/* ── CONCEPT — section de bienvenue ── */}
      <section id="concept" style={{ background: C.white, padding: '96px 32px 80px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>

          {/* Texte intro */}
          <div style={{ maxWidth: 700, marginBottom: 80 }}>
            <motion.p {...fadeUp(0)}
              style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted, marginBottom: 18 }}>
              Bienvenue
            </motion.p>
            <motion.h2 {...fadeUp(0.06)}
              style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.8rem,4vw,2.8rem)', letterSpacing: '-1.5px', color: C.ink, marginBottom: 24, lineHeight: 1.2 }}>
              <>Ta planche, à la journée ou plusieurs jours.<br/>Sur la presqu'île de Quiberon.</>
            </motion.h2>
            <motion.p {...fadeUp(0.12)}
              style={{ fontSize: 16, lineHeight: 1.85, color: C.muted }}>
              <>Réserve un paddle pour une journée, 2 ou 3 jours — ou plus. La planche reste avec toi&nbsp;: pas de retour le soir, pas de queue le matin. Tu pars quand tu veux. Pagaie, pompe, leash, gilet — tout est inclus. Aucune expérience requise.</>
            </motion.p>
          </div>

          {/* Comment ça marche */}
          <div style={{ marginBottom: 80 }}>
            <motion.div {...fadeUp(0)} style={{ marginBottom: 48 }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted, marginBottom: 12 }}>Simple & rapide</p>
              <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.8rem, 4vw, 3rem)', letterSpacing: '-1.5px', color: C.ink, margin: 0 }}>
                Comment ça marche
              </h2>
            </motion.div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 48, marginBottom: 40 }}>
              {[
                { num: '01', title: 'Tu réserves en 2 min', desc: 'Depuis ton transat, en ligne. Tu choisis ta date de départ et ta durée. La planche est bloquée pour toi.' },
                { num: '02', title: 'Tu viens chercher ta planche', desc: 'Une seule fois. On te donne tout — planche, pagaie, pompe, leash, gilet.' },
                { num: '03', title: "Elle est à toi, le temps voulu", desc: 'Une journée, 2 ou 3 jours — ou plus. Pas de retour le soir, pas de queue le matin. Tu pars quand tu veux.' },
                { num: '04', title: 'Tu plouf', desc: "Le coucher de soleil, c'est pour toi. Pendant que les autres ont déjà rendu leur planche." },
              ].map((step, i) => (
                <motion.div key={step.num} {...fadeUp(i * 0.1)}>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 48, color: C.linD, lineHeight: 1, marginBottom: 24, letterSpacing: '-2px' }}>{step.num}</div>
                  <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: 17, color: C.ink, marginBottom: 14, letterSpacing: '-0.3px' }}>{step.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.75, color: C.muted }}>{step.desc}</p>
                </motion.div>
              ))}
            </div>
            <motion.div {...fadeUp(0.2)} style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <motion.button
                onClick={() => scrollToWeeks('Choisissez votre formule')}
                whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.98 }}
                style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '16px 44px', background: C.ink, color: '#fff', border: 'none', borderRadius: 2, cursor: 'pointer' }}>
                Vérifier les disponibilités →
              </motion.button>
            </motion.div>
          </div>

          {/* 3 piliers */}
          <motion.div {...fadeUp(0)} style={{ marginBottom: 40 }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted, marginBottom: 12 }}>Zéro stress</p>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.8rem, 4vw, 3rem)', letterSpacing: '-1.5px', color: C.ink, margin: 0 }}>
              Tout est prévu
            </h2>
          </motion.div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 40, marginBottom: 48 }}>
            {[
              { num: '01', title: 'Planches premium', desc: 'Bluefin — des planches de paddle de haute qualité. Stables, robustes, gonflées et prêtes à l\'eau dès la remise.' },
              { num: '02', title: 'Tout inclus', desc: 'Pagaie réglable, leash, pompe haute pression, sac de transport. Rien à acheter, rien à prévoir.' },
              { num: '03', title: 'Remise en main propre', desc: 'Récupération le jour de ton départ, retour à la fin de ta location, au 17 Rue Docteur Le Gall, Saint-Pierre-Quiberon.' },
            ].map(({ num, title, desc }, i) => (
              <motion.div key={num} {...fadeUp(i * 0.1)} style={{ borderTop: `1.5px solid ${C.border}`, paddingTop: 28 }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 42, color: C.linD, lineHeight: 1, marginBottom: 20, letterSpacing: '-2px' }}>{num}</div>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: 17, color: C.ink, marginBottom: 12, letterSpacing: '-0.3px' }}>{title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.75, color: C.muted }}>{desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp(0.2)} style={{ marginBottom: 72, display: 'flex', justifyContent: 'flex-start' }}>
            <motion.button
              onClick={() => { const el = document.getElementById('planches'); if (el) el.scrollIntoView({ behavior: 'smooth' }) }}
              whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.98 }}
              style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '16px 44px', background: C.ink, color: '#fff', border: 'none', borderRadius: 2, cursor: 'pointer' }}>
              Découvrir nos planches →
            </motion.button>
          </motion.div>

          {/* Bandeau citation + CTA réservation */}
          <motion.div {...fadeUp(0.15)}
            style={{ background: C.lin, borderRadius: 2, padding: 'clamp(32px,5vw,56px) clamp(28px,5vw,60px)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 32 }}>
            <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.1rem,2.2vw,1.55rem)', lineHeight: 1.5, letterSpacing: '-0.4px', color: C.ink, maxWidth: 560, margin: 0 }}>
              <>"Eaux calmes, fonds sableux, panorama sur la baie de Quiberon. La chute est comprise dans le tarif."</>
            </p>
            <motion.button
              onClick={() => scrollToWeeks('Choisissez votre formule')}
              whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.98 }}
              style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '16px 44px', background: C.ink, color: '#fff', border: 'none', borderRadius: 2, cursor: 'pointer', flexShrink: 0 }}>
              Voir les disponibilités →
            </motion.button>
          </motion.div>

        </div>
      </section>

      {/* ── SEMAINES ── */}
      <WeekPicker openModal={openModal} title={weekPickerTitle} initialPromo={partner ? { code: partner.code, sejourOk: true } : null} />

      {/* ── PHOTO PLEINE LARGEUR ── */}
      <div style={{ height: '50vh', minHeight: 280, overflow: 'hidden', position: 'relative' }}>
        <motion.img src={PHOTOS.mid} alt="Côte Quiberon" loading="lazy"
          initial={{ scale: 1.06 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 1.4, ease }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(17,17,17,0.18)' }} />
      </div>

      {/* ── NOS PLANCHES ── */}
      <section id="planches" style={{ background: C.bg, padding: '100px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <motion.div {...fadeUp(0)} style={{ marginBottom: 64 }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted, marginBottom: 12 }}>Nos planches</p>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.8rem, 4vw, 3rem)', letterSpacing: '-1.5px', color: C.ink, margin: 0 }}>
              Bluefin — des planches de paddle de haute qualité
            </h2>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48 }}>
            {[
              {
                model: "Bluefin 10'8\"",
                tag: 'Débutants & Familles',
                desc: "Large et stable, c'est la plus polyvalente. Parfaite pour débuter, pour les enfants et les familles qui veulent explorer en douceur les eaux calmes de la baie de Quiberon.",
                specs: ['325 × 82 × 16 cm','Charge max 150 kg','8 planches disponibles','Construction ESL renforcée'],
                photo: modelesList.find(m => m.id === '10p6')?.photo_url || PHOTOS.board1,
              },
              {
                model: "Bluefin 12'",
                tag: 'Rando & Glisse',
                desc: "Plus longue, elle glisse et tient le cap. Idéale pour les balades plus longues, les grands gabarits, ou pour emmener un enfant devant soi le long de la presqu'île de Quiberon.",
                specs: ['366 × 81 × 15 cm','Charge max 175 kg','2 planches disponibles','Construction ESL renforcée'],
                photo: modelesList.find(m => m.id === '9p5')?.photo_url || PHOTOS.board2,
              },
            ].map((board, i) => (
              <motion.div key={board.model} {...fadeUp(i * 0.12)}>
                <div style={{ aspectRatio: '1/1', overflow: 'hidden', borderRadius: 2, marginBottom: 28, background: 'transparent' }}>
                  <motion.img src={board.photo} alt={board.model}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    whileHover={{ scale: 1.03 }} transition={{ duration: 0.7 }} />
                </div>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.accent, marginBottom: 10 }}>{board.tag}</p>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: 20, color: C.ink, marginBottom: 12, letterSpacing: '-0.3px' }}>{board.model}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.75, color: C.muted, marginBottom: 20 }}>{board.desc}</p>
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px 16px' }}>
                  {board.specs.map(s => (
                    <div key={s} style={{ fontSize: 12, color: C.muted }}>{s}</div>
                  ))}
                </div>
                <button onClick={() => openModal(board.size)}
                  style={{ marginTop: 20, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.ink, background: 'none', border: 'none', cursor: 'pointer', padding: 0, borderBottom: `1.5px solid ${C.ink}`, paddingBottom: 2 }}>
                  Réserver cette planche →
                </button>
              </motion.div>
            ))}
          </div>

          {/* Inclus */}
          <motion.div {...fadeUp(0.1)} style={{ marginTop: 60, padding: '28px 36px', background: C.lin, borderRadius: 2 }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted, marginBottom: 16 }}>Tout inclus</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 40px' }}>
              {['Planche gonflable Bluefin','Pagaie réglable aluminium','Leash de sécurité','Pompe haute pression','Sac de transport'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 3, height: 3, borderRadius: '50%', background: C.accent }} />
                  <span style={{ fontSize: 13, color: C.muted }}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" style={{ background: '#0E4D6B', padding: '100px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>

          {/* Header */}
          <motion.div {...fadeUp(0)} style={{ marginBottom: 56 }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 12 }}>Nous trouver</p>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 300, fontSize: 'clamp(1.8rem, 4vw, 3rem)', letterSpacing: '-1.5px', color: '#fff', margin: 0 }}>
              Saint-Pierre-Quiberon
            </h2>
          </motion.div>

          {/* Carte pleine largeur */}
          <motion.div {...fadeUp(0.08)} style={{ borderRadius: 4, overflow: 'hidden', height: 'clamp(220px, 35vw, 380px)', marginBottom: 40, border: '1px solid rgba(255,255,255,0.1)' }}>
            <iframe title="Carte Quiberon" width="100%" height="100%" style={{ border: 0 }}
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`} />
          </motion.div>

          {/* 4 cards infos */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              {
                icon: '📍',
                label: 'Adresse',
                val: ADDRESS,
                link: MAPS_FALLBACK,
                lt: 'Ouvrir dans Maps →',
              },
              {
                icon: '💬',
                label: 'WhatsApp',
                val: 'Une question ? On répond vite.',
                link: `https://wa.me/${WHATSAPP}`,
                lt: 'Envoyer un message →',
              },
              {
                icon: '📅',
                label: 'Saison',
                val: '20 juin — 30 août 2026\nRemise du matériel le samedi',
                link: null,
              },
              {
                icon: '🌬️',
                label: 'Météo & conditions',
                val: 'Consultez Windy avant de partir.',
                link: 'https://www.windy.com/?47.484,-3.140,12',
                lt: 'Windy — Quiberon →',
              },
            ].map(({ icon, label, val, link, lt }, i) => (
              <motion.div key={label} {...fadeUp(i * 0.08)}
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, padding: '28px 24px' }}>
                <div style={{ fontSize: 22, marginBottom: 14 }}>{icon}</div>
                <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 10 }}>{label}</p>
                {val && <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, whiteSpace: 'pre-line', marginBottom: link ? 12 : 0 }}>{val}</p>}
                {link && (
                  <a href={link} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12, color: '#5BC8E8', textDecoration: 'none', fontWeight: 600, letterSpacing: '0.04em' }}>
                    {lt}
                  </a>
                )}
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: C.ink, padding: '36px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
          <img src="/plouf_go_logo_profil_carre.png" alt="Plouf and Go" style={{ height: 32, width: 'auto', objectFit: 'contain' }} />
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.06em' }}>
            LOCATION PADDLE · SAINT-PIERRE-QUIBERON · 2026
          </div>
          <div style={{ display: 'flex', gap: 28 }}>
            {[['WhatsApp', `https://wa.me/${WHATSAPP}`],['Google Maps', MAPS_FALLBACK],['Windy', 'https://www.windy.com/?47.484,-3.140,12']].map(([l,h]) => (
              <a key={l} href={h} target="_blank" rel="noreferrer"
                style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', textDecoration: 'none', textTransform: 'uppercase' }}>
                {l}
              </a>
            ))}
            <Link to="/cgv"
              style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', textDecoration: 'none', textTransform: 'uppercase' }}>
              CGV
            </Link>
            <Link to="/planning"
              style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.18)', textDecoration: 'none', textTransform: 'uppercase' }}>
              Admin
            </Link>
          </div>
        </div>
      </footer>

      {/* ── STYLE GLOBAL HERO GRID ── */}
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        @media (max-width: 768px) {
          .hero-text-block {
            bottom: auto !important;
            top: 50% !important;
            transform: translateY(10%) !important;
          }
          #concept { padding: 72px 24px 64px !important; }
          #semaines { padding: 72px 20px !important; }
          #planches { padding: 72px 20px !important; }
          #comment  { padding: 72px 20px !important; }
          #contact  { padding: 72px 20px !important; }
        }
      `}</style>
    </div>
  )
}
